import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useTransition } from "react";
import { createPortal } from "react-dom";
import { keyframes } from "@mui/system";
import { Maximize, Minimize } from "lucide-react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckIcon from "@mui/icons-material/Check";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import UndoIcon from "@mui/icons-material/Undo";
import { useModalStyles } from "./useModalStyles";
import {
  buildExcelCsv,
  downloadCsv,
  ExcelSheetColumn,
  ExcelSheetRow,
  filterExcelInputByType,
  formatExcelCurrency,
  formatExcelDate,
  formatCellValue,
  getExcelNestedSource,
  getExcelNestedSubRows,
  getExcelStatusColor,
  getExcelSubRows,
  getExcelVisualLineCount,
  INVOICE_PAY_STATUS_COLORS,
  isValidExcelDate,
  isValidExcelNumber,
  parseExcelNumber,
  sumExcelColumn,
  toColLetters,
} from "../../utils/excelSheet";

export type ExcelSheetSelectedCell = { rowIndex: number; row: ExcelSheetRow; columnId: string };

export type ExcelSheetSelectionAction = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  iconColor?: string;
  onClick: () => void;
};

export type ExcelSheetPopupProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  toolbar?: React.ReactNode | ((exportButton: React.ReactNode, controls: React.ReactNode) => React.ReactNode);
  /** Rendered in the header's action row, just before the Export CSV button. */
  headerActions?: React.ReactNode;
  columns: ExcelSheetColumn[];
  rows?: ExcelSheetRow[];
  loading?: boolean;
  emptyText?: string;
  showExport?: boolean;
  exportFileName?: string;
  /** When set, Export CSV is enabled only while the list has this many records or fewer. */
  maxExportRecords?: number;
  showTotals?: boolean;
  totalsLabel?: (count: number) => string;
  /** Grand totals for all matching records (from the backend summary). */
  totalsValues?: Record<string, unknown>;
  totalRecords?: number;
  onLoadMore?: () => void;
  loadMoreLoading?: boolean;
  loadMoreSize?: number;
  onFetchAllRows?: () => Promise<ExcelSheetRow[]>;
  clearColumnFiltersSignal?: number | string;
  /** Brief spinner when the popup reopens with rows already in memory. */
  reopenLoadingMs?: number;
  forceFullScreen?: boolean;
  refreshing?: boolean;
  highlightRowKey?: string | number | null;
  onRowClick?: (row: ExcelSheetRow, rowIndex: number) => void;
  onCellPopupEdit?: (
    row: ExcelSheetRow,
    rowIndex: number,
    columnId: string,
    subRow?: Record<string, unknown>
  ) => void;
  getRowActions?: (
    row: ExcelSheetRow,
    rowIndex: number
  ) => Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    iconColor?: string;
    iconBackground?: string;
    onClick: () => void;
  }>;
  onFeeEdit?: (
    context: unknown,
    meta: { rowIndex: number; row: ExcelSheetRow; columnId: string; label: string }
  ) => void;
  /** Extra right-click menu items for the current cell selection (dragged range + selected rows/columns). */
  getSelectionActions?: (cells: ExcelSheetSelectedCell[]) => ExcelSheetSelectionAction[];
  onCellChange?: (rowIndex: number, columnId: string, value: string) => void;
  savableColumnIds?: string[];
  onSaveRow?: (params: {
    rowIndex: number;
    row: ExcelSheetRow;
    original: ExcelSheetRow;
    changedFields: Record<string, unknown>;
  }) => Promise<void> | void;
};

const normalizeForCompare = (value: unknown): string => String(value ?? "").trim();

const BODY_ROW_HEIGHT = 34;
const OVERSCAN_PX = 400;
const DEFAULT_VIEWPORT = 900;
const MIN_COL_WIDTH = 60;
const MIN_ROW_LINE_HEIGHT = 22;
const MAX_ROW_LINE_HEIGHT = 200;

const toSheetHeaderLabel = (label: string): string =>
  String(label || "").replace(/amount/gi, "AMT").toUpperCase();

const invoiceVisualCount = (row: ExcelSheetRow, columns: ExcelSheetColumn[]): number =>
  Math.max(1, getExcelVisualLineCount(row, columns));

const cursorFromSvg = (svg: string, hotspotX: number, hotspotY: number): string =>
  `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotspotX} ${hotspotY}, pointer`;

const ARROW_BIG_RIGHT_PATH =
  "M11 9a1 1 0 0 0 1-1V5.061a1 1 0 0 1 1.811-.75l6.836 6.836a1.207 1.207 0 0 1 0 1.707l-6.836 6.835a1 1 0 0 1-1.811-.75V16a1 1 0 0 0-1-1H5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z";
const ARROW_BIG_DOWN_PATH =
  "M15 11a1 1 0 0 0 1 1h2.939a1 1 0 0 1 .75 1.811l-6.835 6.836a1.207 1.207 0 0 1-1.707 0L4.31 13.81a1 1 0 0 1 .75-1.811H8a1 1 0 0 0 1-1V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1z";

const arrowCursorSvg = (path: string, fill: string, stroke: string): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}" stroke-width="0.5"><path d="${path}"/></svg>`;

/** Excel-like: lucide "arrow-big-right" icon, shown when hovering a row number (click selects the row). */
const ROW_SELECT_CURSOR_LIGHT = cursorFromSvg(arrowCursorSvg(ARROW_BIG_RIGHT_PATH, "black", "white"), 13, 8);
const ROW_SELECT_CURSOR_DARK = cursorFromSvg(arrowCursorSvg(ARROW_BIG_RIGHT_PATH, "white", "black"), 13, 8);

/** Excel-like: lucide "arrow-big-down" icon, shown when hovering a column header (click selects the column). */
const COLUMN_SELECT_CURSOR_LIGHT = cursorFromSvg(arrowCursorSvg(ARROW_BIG_DOWN_PATH, "black", "white"), 8, 13);
const COLUMN_SELECT_CURSOR_DARK = cursorFromSvg(arrowCursorSvg(ARROW_BIG_DOWN_PATH, "white", "black"), 8, 13);

const FILTER_BLANK_KEY = "__EXCEL_BLANK__";

type SortState = { columnId: string; direction: "asc" | "desc" };

const getColumnSortValue = (row: ExcelSheetRow, col: ExcelSheetColumn): number | string => {
  const value = row[col.id];
  if (col.type === "currency" || col.type === "number" || col.type === "percent") {
    return parseExcelNumber(value);
  }
  if (col.type === "date") {
    const time = new Date(String(value ?? "")).getTime();
    return Number.isFinite(time) ? time : 0;
  }
  return String(value ?? "").toLowerCase();
};

const getColumnFilterKey = (row: ExcelSheetRow, columnId: string): string => {
  const value = row[columnId];
  if (value === undefined || value === null || String(value).trim() === "") return FILTER_BLANK_KEY;
  return String(value);
};

const getSortLabels = (col: ExcelSheetColumn): { asc: string; desc: string } => {
  if (col.type === "date") return { asc: "Sort Oldest to Newest", desc: "Sort Newest to Oldest" };
  if (col.type === "currency" || col.type === "number" || col.type === "percent") {
    return { asc: "Sort Smallest to Largest", desc: "Sort Largest to Smallest" };
  }
  return { asc: "Sort A to Z", desc: "Sort Z to A" };
};

type SelectionBounds = {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
  lineMin?: number;
  lineMax?: number;
};

type ExcelInvoiceRowProps = {
  row: ExcelSheetRow;
  rowIndex: number;
  excelRowStart: number;
  columns: ExcelSheetColumn[];
  cellBaseSx: Record<string, unknown>;
  columnCellSx: (col: ExcelSheetColumn) => Record<string, unknown>;
  gridLine: string;
  rowNumBg: string;
  editableColor: string;
  readOnlyColor: string;
  isDark: boolean;
  indexColWidth: number;
  onCellChange: (rowIndex: number, columnId: string, value: string) => void;
  onCellBlur: (rowIndex: number, column: ExcelSheetColumn, value: string) => void;
  isDirty: boolean;
  isSaving: boolean;
  errorMessage?: string;
  onSaveRow?: (rowIndex: number) => void;
  onCancelRow?: (rowIndex: number) => void;
  editingColumnId: string | null;
  invalidColumnId: string | null;
  invalidMessage?: string;
  selectionBounds: SelectionBounds | null;
  showEditIcon: boolean;
  highlightRowKey?: string | number | null;
  onRowClick?: (rowIndex: number) => void;
  onSelectRow?: (rowIndex: number) => void;
  hasRowActions: boolean;
  onRowContextMenu?: (event: React.MouseEvent, rowIndex: number) => void;
  onPopupEditClick?: (rowIndex: number, columnId: string, subRow?: Record<string, unknown>) => void;
  onCellMouseDown: (event: React.MouseEvent, rowIndex: number, colIndex: number, lineIndex?: number) => void;
  onCellMouseEnter: (rowIndex: number, colIndex: number, lineIndex?: number) => void;
  onCellKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, column: ExcelSheetColumn) => void;
  onFeeContextMenu: (
    event: React.MouseEvent,
    label: string,
    context: unknown,
    rowIndex: number,
    columnId: string,
    amount: string,
    secondary?: { label: string; context: unknown; icon?: "paid" | "edit" }
  ) => void;
  onCellContextMenu: (
    event: React.MouseEvent,
    rowIndex: number,
    colIndex: number,
    col: ExcelSheetColumn,
    cellDisplayValue: string,
    canEdit: boolean,
    subRow?: Record<string, unknown>,
    lineIndex?: number
  ) => void;
  onFeeClick: (
    event: React.MouseEvent,
    label: string,
    context: unknown,
    rowIndex: number,
    columnId: string,
    amount: string,
    lineIndex?: number,
    secondary?: { label: string; context: unknown; icon?: "paid" | "edit" }
  ) => void;
  activeFeeCell: {
    rowIndex: number;
    columnId: string;
    lineIndex?: number;
    secondary?: { label: string; context: unknown; icon?: "paid" | "edit" };
  } | null;
  onInlineFeeEditClick: (event: React.MouseEvent) => void;
  onInlineFeeEditSecondaryClick: (event: React.MouseEvent) => void;
  onFeeEditDirect?: (
    context: unknown,
    meta: { rowIndex: number; row: ExcelSheetRow; columnId: string; label: string }
  ) => void;
  accent: string;
  lineHeight: number;
  onRowResizeMouseDown: (event: React.MouseEvent, rowIndex: number) => void;
  frozenColumnIndex: number;
  /** Set when this row is one of the frozen top rows — pins it below the header at this offset. */
  stickyTop?: number;
  frozenCellBg: string;
  isRowLocked: boolean;
  lockedCols: Set<number>;
  /** Columns of this row picked with Ctrl/Cmd+click — drawn like the dragged selection. */
  pickedCols?: Set<number>;
  lockedCellBg: string;
  lockedIndicatorBg: string;
};

const ExcelInvoiceRow = React.memo(function ExcelInvoiceRow({
  row,
  rowIndex,
  excelRowStart,
  columns,
  cellBaseSx,
  columnCellSx,
  gridLine,
  rowNumBg,
  editableColor,
  readOnlyColor,
  isDark,
  indexColWidth,
  onCellChange,
  onCellBlur,
  isDirty,
  isSaving,
  errorMessage,
  onSaveRow,
  onCancelRow,
  editingColumnId,
  invalidColumnId,
  invalidMessage,
  selectionBounds,
  showEditIcon,
  highlightRowKey,
  onRowClick,
  onSelectRow,
  hasRowActions,
  onRowContextMenu,
  onPopupEditClick,
  onCellMouseDown,
  onCellMouseEnter,
  onCellKeyDown,
  onFeeContextMenu,
  onCellContextMenu,
  onFeeClick,
  activeFeeCell,
  onInlineFeeEditClick,
  onInlineFeeEditSecondaryClick,
  onFeeEditDirect,
  accent,
  lineHeight,
  onRowResizeMouseDown,
  frozenColumnIndex,
  stickyTop,
  frozenCellBg,
  isRowLocked,
  lockedCols,
  pickedCols,
  lockedCellBg,
  lockedIndicatorBg,
}: ExcelInvoiceRowProps) {
  const isFrozenRow = stickyTop !== undefined;

  /*
   * IMPORTANT:
   *
   * Sticky table cells can lose their border-bottom while the scroll
   * container is scrolling because table borders participate in table
   * border painting rather than the sticky element's own composited layer.
   *
   * We therefore use an inset box-shadow for frozen rows.
   */
  const stickyRowBorderShadow = isFrozenRow
    ? `inset 0 -1px 0 ${gridLine}`
    : "none";

  const rowStickySx = isFrozenRow
    ? {
        position: "sticky" as const,
        top: stickyTop,
        zIndex: 2,
        backgroundClip: "padding-box"
      }
    : {};

  const subRows = getExcelSubRows(row, columns);
  const nestedSource = getExcelNestedSource(columns);
  const visualLineCount = invoiceVisualCount(row, columns);

  const [isRowHovered, setIsRowHovered] = useState(false);

  const editAccent = isDark ? "#818cf8" : "#4f46e5";

  const stackedCellSx = {
    height: lineHeight,
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  };

  const rowHoverBg = isDark
    ? "rgba(148,163,184,0.08)"
    : "rgba(15,23,42,0.035)";

  const isHighlighted =
    highlightRowKey !== null &&
    highlightRowKey !== undefined &&
    String(row.invoiceId ?? "") === String(highlightRowKey);

  const highlightBg = isDark
    ? "rgba(34,197,94,0.24)"
    : "rgba(34,197,94,0.16)";

  const getCellBoxShadow = ({
    isInRange,
    isAnchorCell,
    isInvalid,
    isEditingThisCell,
  }: {
    isInRange?: boolean;
    isAnchorCell?: boolean;
    isInvalid?: boolean;
    isEditingThisCell?: boolean;
  }) => {
    const shadows: string[] = [];

    if (isInvalid) {
      shadows.push("inset 0 0 0 1.5px #dc3545");
    }

    if (isEditingThisCell) {
      shadows.push(
        `inset 0 0 0 2px ${isDark ? "#2dd4bf" : "#0d9488"}`
      );
    }

    if (isInRange) {
      shadows.push(
        `inset 0 0 0 ${isAnchorCell ? "1.5px" : "1px"} ${accent}`
      );
    }

    if (isFrozenRow) {
      shadows.push(stickyRowBorderShadow);
    }

    return shadows.length > 0 ? shadows.join(", ") : "none";
  };

  return (
    <TableRow
      data-excel-invoice-index={rowIndex}
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
    >
      <TableCell
        sx={{
          ...cellBaseSx,

          width: indexColWidth,
          minWidth: indexColWidth,
          maxWidth: indexColWidth,

          height: "auto",
          py: 0,

          bgcolor: isHighlighted ? highlightBg : isRowLocked ? lockedIndicatorBg : rowNumBg,

          transition: "background-color 0.4s ease",

          color: "inherit",
          fontWeight: 700,
          textAlign: "right",

          position: "sticky",
          left: 0,
          top: stickyTop,

          zIndex: isFrozenRow ? 3 : 1,
          ...(isFrozenRow
            ? {
                backgroundClip: "padding-box",
                borderBottom: "none",
                boxShadow: stickyRowBorderShadow,
              }
            : {}),
        }}
      >
        {isDirty || isSaving || errorMessage ? (
          <Box
            sx={{
              height: lineHeight * visualLineCount,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.25,
            }}
          >
            <Box
              component="span"
              sx={{
                color: isDark
                  ? "rgba(226,232,240,0.7)"
                  : "#64748b",
                fontWeight: 700,
                mr: 0.25,
              }}
            >
              {excelRowStart}
            </Box>

            {isSaving ? (
              <CircularProgress
                size={14}
                sx={{ color: editableColor }}
                aria-label={`Saving row ${excelRowStart}`}
              />
            ) : (
              <>
                <IconButton
                  aria-label={`Save row ${excelRowStart}`}
                  title={errorMessage || "Save"}
                  size="small"
                  onClick={() => onSaveRow?.(rowIndex)}
                  sx={{
                    p: 0.25,
                    color: errorMessage ? "#dc3545" : "#28a745",
                  }}
                >
                  {errorMessage ? (
                    <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>

                <IconButton
                  aria-label={`Discard changes to row ${excelRowStart}`}
                  title="Discard changes"
                  size="small"
                  onClick={() => onCancelRow?.(rowIndex)}
                  sx={{
                    p: 0.25,
                    color: isDark
                      ? "rgba(226,232,240,0.7)"
                      : "#64748b",
                  }}
                >
                  <UndoIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </>
            )}
          </Box>
        ) : (
          <Tooltip
            title={hasRowActions ? "Right-click for actions" : ""}
            arrow
            placement="bottom"
            disableHoverListener={!hasRowActions}
            enterDelay={500}
            slotProps={{
              popper: {
                modifiers: [{ name: "offset", options: { offset: [0, -6] } }],
              },
            }}
          >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onSelectRow?.(rowIndex);
              onRowClick?.(rowIndex);
            }}
            onContextMenu={
              hasRowActions
                ? (e) => onRowContextMenu?.(e, rowIndex)
                : undefined
            }
            sx={{
              position: "relative",
              height: lineHeight * visualLineCount,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              cursor: isDark
                ? ROW_SELECT_CURSOR_DARK
                : ROW_SELECT_CURSOR_LIGHT,
            }}
          >
            <Box
              component="span"
              sx={{
                color: isDark
                  ? "rgba(226,232,240,0.7)"
                  : "#64748b",
              }}
            >
              {excelRowStart}
            </Box>

            {showEditIcon ? (
              <Tooltip
                title={onRowClick ? "Edit Invoice" : ""}
                arrow
                disableHoverListener={!onRowClick}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, -6] },
                      },
                    ],
                  },
                }}
              >
                <Box
                  aria-label={`Row ${excelRowStart} has editable fields`}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    opacity: isRowHovered ? 1 : 0,
                    transition: "opacity 0.12s ease",
                    bgcolor: accent,
                    color: "#fff",
                    boxShadow: isDark
                      ? "0 2px 6px rgba(0,0,0,0.45)"
                      : "0 2px 6px rgba(15,23,42,0.25)",
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 13 }} />
                </Box>
              </Tooltip>
            ) : null}
          </Box>
          </Tooltip>
        )}

        <Box
          onMouseDown={(e) => onRowResizeMouseDown(e, rowIndex)}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 6,
            cursor: "row-resize",
            zIndex: 5,
            "&:hover": {
              backgroundColor: accent,
            },
          }}
        />
      </TableCell>

      {columns.map((col, colIndex) => {
        const isInRange =
          (!!selectionBounds &&
            rowIndex >= selectionBounds.minRow &&
            rowIndex <= selectionBounds.maxRow &&
            colIndex >= selectionBounds.minCol &&
            colIndex <= selectionBounds.maxCol) ||
          !!pickedCols?.has(colIndex);

        const isAnchorCell =
          !!selectionBounds &&
          selectionBounds.minRow === selectionBounds.maxRow &&
          selectionBounds.minCol === selectionBounds.maxCol &&
          rowIndex === selectionBounds.minRow &&
          colIndex === selectionBounds.minCol;

        if (col.subRowsFrom) {
          const isSingleCellLineSelection =
            !!selectionBounds &&
            selectionBounds.lineMin !== undefined &&
            selectionBounds.lineMax !== undefined &&
            selectionBounds.minRow === rowIndex &&
            selectionBounds.maxRow === rowIndex &&
            selectionBounds.minCol === colIndex &&
            selectionBounds.maxCol === colIndex;

          let lineCounter = -1;

          const isFrozenColumn =
            frozenColumnIndex >= 0 &&
            colIndex <= frozenColumnIndex;

          return (
            <TableCell
              key={col.id}
              onMouseDown={(e) =>
                onCellMouseDown(e, rowIndex, colIndex)
              }
              onMouseEnter={() =>
                onCellMouseEnter(rowIndex, colIndex)
              }
              sx={{
                ...columnCellSx(col),
                ...rowStickySx,

                zIndex:
                  isFrozenRow && isFrozenColumn
                    ? 3
                    : isFrozenRow || isFrozenColumn
                      ? 2
                      : undefined,

                height: "auto",
                py: 0,
                verticalAlign: "top",

                cursor: "cell",
                userSelect: "none",

                transition: "background-color 0.4s ease",

                bgcolor: isHighlighted
                  ? highlightBg
                  : isInRange && !isSingleCellLineSelection
                    ? isDark
                      ? "rgba(45,212,191,0.14)"
                      : "rgba(13,148,136,0.10)"
                    : isRowLocked || lockedCols.has(colIndex)
                      ? lockedCellBg
                      : isFrozenRow || isFrozenColumn
                        ? isRowHovered
                          ? rowNumBg
                          : frozenCellBg
                        : isRowHovered
                          ? rowHoverBg
                          : undefined,

                /*
                 * IMPORTANT:
                 * Frozen rows use inset shadow instead of relying
                 * on borderBottom.
                 */
                ...(isFrozenRow
                  ? {
                      backgroundClip: "padding-box",
                      borderBottom: "none",
                    }
                  : {}),

                boxShadow: isSingleCellLineSelection
                  ? "none"
                  : getCellBoxShadow({
                      isInRange,
                    }),
              }}
            >
              {subRows.map((subRow, subIndex) => {
                const nestedRows = col.nestedSubRowsFrom
                  ? getExcelNestedSubRows(
                      subRow,
                      col.nestedSubRowsFrom
                    )
                  : null;

                if (nestedRows) {
                  return (
                    <React.Fragment
                      key={`${col.id}-${subIndex}`}
                    >
                      {nestedRows.map(
                        (nestedRow, nestedIndex) => {
                          const value = nestedRow[col.id];

                          const hasValue =
                            value !== null &&
                            value !== undefined &&
                            value !== "";

                          const display = hasValue
                            ? formatCellValue(col, value)
                            : "";

                          const statusColor =
                            getExcelStatusColor(
                              col,
                              row,
                              subRow,
                              nestedRow
                            );

                          const hasFeeAction =
                            !!col.rightClickTooltip && hasValue;

                          const feeContext =
                            col.feeContextField
                              ? nestedRow?.[
                                  col.feeContextField
                                ]
                              : undefined;

                          const secondaryContext =
                            col.secondaryAction && hasValue
                              ? nestedRow?.[
                                  col.secondaryAction.contextField
                                ]
                              : undefined;

                          const isLastVisual =
                            subIndex === subRows.length - 1 &&
                            nestedIndex ===
                              nestedRows.length - 1;

                          lineCounter += 1;

                          const lineIndex = lineCounter;

                          const isLineSelected =
                            isSingleCellLineSelection &&
                            lineIndex >=
                              selectionBounds!.lineMin! &&
                            lineIndex <=
                              selectionBounds!.lineMax!;

                          const isLineAnchor =
                            isLineSelected &&
                            selectionBounds!.lineMin ===
                              selectionBounds!.lineMax;

                          return (
                            <Box
                              key={`${col.id}-${subIndex}-${nestedIndex}`}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                onCellMouseDown(
                                  e,
                                  rowIndex,
                                  colIndex,
                                  lineIndex
                                );
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                onCellMouseEnter(
                                  rowIndex,
                                  colIndex,
                                  lineIndex
                                );
                              }}
                              onClick={
                                hasFeeAction
                                  ? (e) => {
                                      e.stopPropagation();

                                      onFeeClick(
                                        e,
                                        col.rightClickTooltip!,
                                        feeContext,
                                        rowIndex,
                                        col.id,
                                        display,
                                        lineIndex,
                                        col.secondaryAction
                                          ? {
                                              label:
                                                col.secondaryAction
                                                  .tooltip,
                                              context:
                                                secondaryContext,
                                              icon:
                                                col.secondaryAction
                                                  .icon,
                                            }
                                          : undefined
                                      );
                                    }
                                  : undefined
                              }
                              onContextMenu={
                                hasFeeAction
                                  ? (e) => {
                                      e.stopPropagation();

                                      onFeeContextMenu(
                                        e,
                                        col.rightClickTooltip!,
                                        feeContext,
                                        rowIndex,
                                        col.id,
                                        display,
                                        col.secondaryAction
                                          ? {
                                              label:
                                                col.secondaryAction
                                                  .tooltip,
                                              context:
                                                secondaryContext,
                                              icon:
                                                col.secondaryAction
                                                  .icon,
                                            }
                                          : undefined
                                      );
                                    }
                                  : undefined
                              }
                              sx={{
                                ...stackedCellSx,

                                mx: -1,
                                px: 1,

                                justifyContent:
                                  col.align === "right"
                                    ? "flex-end"
                                    : "flex-start",

                                borderBottom: isLastVisual
                                  ? "none"
                                  : `1px solid ${gridLine}`,

                                color:
                                  statusColor || "inherit",

                                fontWeight: statusColor
                                  ? 800
                                  : 400,

                                cursor: hasFeeAction
                                  ? "context-menu"
                                  : "cell",

                                bgcolor: isLineSelected
                                  ? isDark
                                    ? "rgba(45,212,191,0.18)"
                                    : "rgba(13,148,136,0.13)"
                                  : undefined,

                                outline: isLineSelected
                                  ? isLineAnchor
                                    ? `1.5px solid ${accent}`
                                    : `1px solid ${accent}`
                                  : "none",

                                outlineOffset: isLineAnchor
                                  ? "-1.5px"
                                  : "-1px",

                                zIndex: isLineSelected
                                  ? 1
                                  : undefined,

                                position: "relative",
                              }}
                              style={
                                statusColor
                                  ? { color: statusColor }
                                  : undefined
                              }
                            >
                              {col.alwaysShowFeeIcon &&
                              hasFeeAction ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.4,
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {display}
                                  </Box>

                                  <Tooltip
                                    title={`Edit ${col.rightClickTooltip}`}
                                    arrow
                                  >
                                    <Box
                                      onClick={(e) => {
                                        e.stopPropagation();

                                        onFeeEditDirect?.(
                                          feeContext,
                                          {
                                            rowIndex,
                                            row,
                                            columnId: col.id,
                                            label:
                                              col.rightClickTooltip!,
                                          }
                                        );
                                      }}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent:
                                          "center",
                                        flexShrink: 0,
                                        cursor: "pointer",
                                        color: isDark
                                          ? "rgba(226,232,240,0.45)"
                                          : "rgba(15,23,42,0.35)",
                                      }}
                                    >
                                      {col.feeIcon === "edit" ? (
                                        <EditOutlinedIcon
                                          sx={{
                                            fontSize: 13,
                                          }}
                                        />
                                      ) : (
                                        <PaidOutlinedIcon
                                          sx={{
                                            fontSize: 13,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Tooltip>

                                  {col.secondaryAction ? (
                                    <Tooltip
                                      title={
                                        col.secondaryAction.tooltip
                                      }
                                      arrow
                                    >
                                      <Box
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          onFeeEditDirect?.(
                                            secondaryContext,
                                            {
                                              rowIndex,
                                              row,
                                              columnId: `${col.id}__secondary`,
                                              label:
                                                col
                                                  .secondaryAction!
                                                  .tooltip,
                                            }
                                          );
                                        }}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent:
                                            "center",
                                          flexShrink: 0,
                                          cursor: "pointer",
                                          color: isDark
                                            ? "rgba(226,232,240,0.45)"
                                            : "rgba(15,23,42,0.35)",
                                        }}
                                      >
                                        {col.secondaryAction
                                          .icon === "edit" ? (
                                          <EditOutlinedIcon
                                            sx={{
                                              fontSize: 13,
                                            }}
                                          />
                                        ) : (
                                          <PaidOutlinedIcon
                                            sx={{
                                              fontSize: 13,
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </Tooltip>
                                  ) : null}
                                </Box>
                              ) : activeFeeCell &&
                                activeFeeCell.rowIndex ===
                                  rowIndex &&
                                activeFeeCell.columnId ===
                                  col.id &&
                                activeFeeCell.lineIndex ===
                                  lineIndex ? (
                                <Box
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onInlineFeeEditClick(e);
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    cursor: "pointer",
                                  }}
                                >
                                  <Tooltip
                                    title={`Edit ${col.rightClickTooltip}`}
                                    arrow
                                  >
                                    <Box
                                      sx={{
                                        width: 22,
                                        height: 22,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent:
                                          "center",
                                        borderRadius: "50%",
                                        bgcolor:
                                          col.feeIcon ===
                                          "edit"
                                            ? editAccent
                                            : accent,
                                        color: "#fff",
                                      }}
                                    >
                                      {col.feeIcon ===
                                      "edit" ? (
                                        <EditOutlinedIcon
                                          sx={{
                                            fontSize: 15,
                                          }}
                                        />
                                      ) : (
                                        <PaidOutlinedIcon
                                          sx={{
                                            fontSize: 15,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Tooltip>

                                  {activeFeeCell.secondary ? (
                                    <Tooltip
                                      title={
                                        activeFeeCell.secondary
                                          .label
                                      }
                                      arrow
                                    >
                                      <Box
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onInlineFeeEditSecondaryClick(
                                            e
                                          );
                                        }}
                                        sx={{
                                          width: 22,
                                          height: 22,
                                          flexShrink: 0,
                                          display: "flex",
                                          alignItems:
                                            "center",
                                          justifyContent:
                                            "center",
                                          borderRadius: "50%",
                                          bgcolor:
                                            activeFeeCell
                                              .secondary
                                              .icon ===
                                            "edit"
                                              ? editAccent
                                              : accent,
                                          color: "#fff",
                                        }}
                                      >
                                        {activeFeeCell
                                          .secondary.icon ===
                                        "edit" ? (
                                          <EditOutlinedIcon
                                            sx={{
                                              fontSize: 15,
                                            }}
                                          />
                                        ) : (
                                          <PaidOutlinedIcon
                                            sx={{
                                              fontSize: 15,
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </Tooltip>
                                  ) : null}

                                  {display}
                                </Box>
                              ) : (
                                display
                              )}
                            </Box>
                          );
                        }
                      )}
                    </React.Fragment>
                  );
                }

                const value = subRow[col.id];

                const hasValue =
                  value !== null &&
                  value !== undefined &&
                  value !== "";

                const display = hasValue
                  ? formatCellValue(col, value)
                  : "";

                const statusColor =
                  getExcelStatusColor(
                    col,
                    row,
                    subRow
                  );

                const hasFeeAction =
                  !!col.rightClickTooltip && hasValue;

                const isPopupEdit =
                  !!col.popupEdit &&
                  !!onPopupEditClick;

                const dueAmount = col.dueField
                  ? parseExcelNumber((row as any)[col.dueField])
                  : undefined;

                const hasDue =
                  dueAmount !== undefined &&
                  dueAmount > 0.004;

                const isLastSubRow =
                  subIndex === subRows.length - 1;

                const showDuePopupEdit =
                  hasDue && isLastSubRow;

                const canPopupEdit =
                  isPopupEdit &&
                  (col.dueField
                    ? showDuePopupEdit
                    : col.popupEditIconWhenEmpty
                      ? !hasValue
                      : hasValue);

                const feeContext =
                  col.feeContextField
                    ? subRow?.[col.feeContextField]
                    : undefined;

                const secondaryContext =
                  col.secondaryAction && hasValue
                    ? subRow?.[
                        col.secondaryAction.contextField
                      ]
                    : undefined;

                const nestedCount =
                  getExcelNestedSubRows(
                    subRow,
                    nestedSource
                  ).length;

                lineCounter += 1;

                const lineIndex = lineCounter;

                const isLineSelected =
                  isSingleCellLineSelection &&
                  lineIndex >=
                    selectionBounds!.lineMin! &&
                  lineIndex <=
                    selectionBounds!.lineMax!;

                const isLineAnchor =
                  isLineSelected &&
                  selectionBounds!.lineMin ===
                    selectionBounds!.lineMax;

                return (
                  <Box
                    key={`${col.id}-${subIndex}`}
                    data-row-index={rowIndex}
                    data-col-index={colIndex}
                    data-sub-index={subIndex}
                    onMouseDown={(e) => {
                      e.stopPropagation();

                      onCellMouseDown(
                        e,
                        rowIndex,
                        colIndex,
                        lineIndex
                      );
                    }}
                    onMouseEnter={(e) => {
                      e.stopPropagation();

                      onCellMouseEnter(
                        rowIndex,
                        colIndex,
                        lineIndex
                      );
                    }}
                    onClick={
                      hasFeeAction
                        ? (e) => {
                            e.stopPropagation();

                            onFeeClick(
                              e,
                              col.rightClickTooltip!,
                              feeContext,
                              rowIndex,
                              col.id,
                              display,
                              lineIndex,
                              col.secondaryAction
                                ? {
                                    label:
                                      col.secondaryAction
                                        .tooltip,
                                    context:
                                      secondaryContext,
                                    icon:
                                      col.secondaryAction
                                        .icon,
                                  }
                                : undefined
                            );
                          }
                        : undefined
                    }
                    onContextMenu={
                      hasFeeAction
                        ? (e) => {
                            e.stopPropagation();

                            onFeeContextMenu(
                              e,
                              col.rightClickTooltip!,
                              feeContext,
                              rowIndex,
                              col.id,
                              display,
                              col.secondaryAction
                                ? {
                                    label:
                                      col.secondaryAction
                                        .tooltip,
                                    context:
                                      secondaryContext,
                                    icon:
                                      col.secondaryAction
                                        .icon,
                                  }
                                : undefined
                            );
                          }
                        : (e) =>
                            onCellContextMenu(
                              e,
                              rowIndex,
                              colIndex,
                              col,
                              display,
                              canPopupEdit,
                              subRow,
                              lineIndex
                            )
                    }
                    sx={{
                      ...stackedCellSx,

                      mx: -1,
                      px: 1,

                      height:
                        lineHeight * nestedCount,

                      justifyContent:
                        !hasValue &&
                        col.popupEditIconWhenEmpty
                          ? "flex-end"
                          : col.align === "right"
                            ? "flex-end"
                            : "flex-start",

                      borderBottom:
                        subIndex <
                        subRows.length - 1
                          ? `1px solid ${gridLine}`
                          : "none",

                      color:
                        statusColor || "inherit",

                      fontWeight: statusColor
                        ? 800
                        : 400,

                      cursor: hasFeeAction
                        ? "context-menu"
                        : "cell",

                      bgcolor: isLineSelected
                        ? isDark
                          ? "rgba(45,212,191,0.18)"
                          : "rgba(13,148,136,0.13)"
                        : undefined,

                      outline: isLineSelected
                        ? isLineAnchor
                          ? `1.5px solid ${accent}`
                          : `1px solid ${accent}`
                        : "none",

                      outlineOffset: isLineAnchor
                        ? "-1.5px"
                        : "-1px",

                      zIndex: isLineSelected
                        ? 1
                        : undefined,

                      position: "relative",
                    }}
                    style={
                      statusColor
                        ? { color: statusColor }
                        : undefined
                    }
                  >
                    {isPopupEdit ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.4,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {display}
                        </Box>

                        {canPopupEdit ? (
                          <EditOutlinedIcon
                            onClick={(e) => {
                              e.stopPropagation();

                              onPopupEditClick?.(
                                rowIndex,
                                col.id,
                                subRow
                              );
                            }}
                            sx={{
                              fontSize: 13,
                              flexShrink: 0,
                              cursor: "pointer",
                              color: isDark
                                ? "rgba(226,232,240,0.45)"
                                : "rgba(15,23,42,0.35)",
                            }}
                          />
                        ) : null}
                      </Box>
                    ) : col.alwaysShowFeeIcon &&
                      hasFeeAction ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.4,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {display}
                        </Box>

                        <Tooltip
                          title={`Edit ${col.rightClickTooltip}`}
                          arrow
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();

                              onFeeEditDirect?.(
                                feeContext,
                                {
                                  rowIndex,
                                  row,
                                  columnId: col.id,
                                  label:
                                    col.rightClickTooltip!,
                                }
                              );
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                "center",
                              flexShrink: 0,
                              cursor: "pointer",
                              color: isDark
                                ? "rgba(226,232,240,0.45)"
                                : "rgba(15,23,42,0.35)",
                            }}
                          >
                            {col.feeIcon === "edit" ? (
                              <EditOutlinedIcon
                                sx={{
                                  fontSize: 13,
                                }}
                              />
                            ) : (
                              <PaidOutlinedIcon
                                sx={{
                                  fontSize: 13,
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>

                        {col.secondaryAction ? (
                          <Tooltip
                            title={
                              col.secondaryAction
                                .tooltip
                            }
                            arrow
                          >
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();

                                onFeeEditDirect?.(
                                  secondaryContext,
                                  {
                                    rowIndex,
                                    row,
                                    columnId: `${col.id}__secondary`,
                                    label:
                                      col
                                        .secondaryAction!
                                        .tooltip,
                                  }
                                );
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  "center",
                                flexShrink: 0,
                                cursor: "pointer",
                                color: isDark
                                  ? "rgba(226,232,240,0.45)"
                                  : "rgba(15,23,42,0.35)",
                              }}
                            >
                              {col.secondaryAction
                                .icon === "edit" ? (
                                <EditOutlinedIcon
                                  sx={{
                                    fontSize: 13,
                                  }}
                                />
                              ) : (
                                <PaidOutlinedIcon
                                  sx={{
                                    fontSize: 13,
                                  }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        ) : null}
                      </Box>
                    ) : activeFeeCell &&
                      activeFeeCell.rowIndex ===
                        rowIndex &&
                      activeFeeCell.columnId ===
                        col.id &&
                      activeFeeCell.lineIndex ===
                        lineIndex ? (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          onInlineFeeEditClick(e);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                        }}
                      >
                        <Tooltip
                          title={`Edit ${col.rightClickTooltip}`}
                          arrow
                        >
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent:
                                "center",
                              borderRadius: "50%",
                              bgcolor:
                                col.feeIcon === "edit"
                                  ? editAccent
                                  : accent,
                              color: "#fff",
                            }}
                          >
                            {col.feeIcon === "edit" ? (
                              <EditOutlinedIcon
                                sx={{
                                  fontSize: 15,
                                }}
                              />
                            ) : (
                              <PaidOutlinedIcon
                                sx={{
                                  fontSize: 15,
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>

                        {activeFeeCell.secondary ? (
                          <Tooltip
                            title={
                              activeFeeCell.secondary
                                .label
                            }
                            arrow
                          >
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();

                                onInlineFeeEditSecondaryClick(
                                  e
                                );
                              }}
                              sx={{
                                width: 22,
                                height: 22,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  "center",
                                borderRadius: "50%",
                                bgcolor:
                                  activeFeeCell
                                    .secondary.icon ===
                                  "edit"
                                    ? editAccent
                                    : accent,
                                color: "#fff",
                              }}
                            >
                              {activeFeeCell.secondary
                                .icon === "edit" ? (
                                <EditOutlinedIcon
                                  sx={{
                                    fontSize: 15,
                                  }}
                                />
                              ) : (
                                <PaidOutlinedIcon
                                  sx={{
                                    fontSize: 15,
                                  }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        ) : null}

                        {display}
                      </Box>
                    ) : (
                      display
                    )}
                  </Box>
                );
              })}
            </TableCell>
          );
        }

        /*
         * ============================================================
         * NORMAL CELL
         * ============================================================
         */

        const visualRow = excelRowStart;

        const value = row[col.id];

        const hasValue =
          value !== null &&
          value !== undefined &&
          value !== "";

        const display = hasValue
          ? formatCellValue(col, value)
          : "";

        const isEditable = !!col.editable;

        const isEditingThisCell =
          isEditable &&
          editingColumnId === col.id;

        const isInvalid =
          invalidColumnId === col.id;

        const statusColor =
          getExcelStatusColor(col, row);

        const hasFeeAction =
          !!col.rightClickTooltip && hasValue;

        const isPopupEdit =
          !!col.popupEdit &&
          !!onPopupEditClick;

        const canPopupEdit =
          isPopupEdit &&
          (col.popupEditIconWhenEmpty ? !hasValue : hasValue);

        const feeContext =
          col.feeContextField
            ? row?.[col.feeContextField]
            : undefined;

        const secondaryContext =
          col.secondaryAction && hasValue
            ? row?.[
                col.secondaryAction.contextField
              ]
            : undefined;

        const isFrozenColumn =
          frozenColumnIndex >= 0 &&
          colIndex <= frozenColumnIndex;

        return (
          <TableCell
            key={col.id}
            data-row-index={rowIndex}
            data-col-index={colIndex}
            title={
              isInvalid
                ? invalidMessage
                : undefined
            }
            onMouseDown={(e) =>
              onCellMouseDown(
                e,
                rowIndex,
                colIndex
              )
            }
            onMouseEnter={() =>
              onCellMouseEnter(
                rowIndex,
                colIndex
              )
            }
            onClick={
              hasFeeAction
                ? (e) =>
                    onFeeClick(
                      e,
                      col.rightClickTooltip!,
                      feeContext,
                      rowIndex,
                      col.id,
                      display
                    )
                : undefined
            }
            onContextMenu={
              hasFeeAction
                ? (e) =>
                    onFeeContextMenu(
                      e,
                      col.rightClickTooltip!,
                      feeContext,
                      rowIndex,
                      col.id,
                      display,
                      col.secondaryAction
                        ? {
                            label:
                              col.secondaryAction
                                .tooltip,
                            context:
                              secondaryContext,
                            icon:
                              col.secondaryAction
                                .icon,
                          }
                        : undefined
                    )
                : (e) =>
                    onCellContextMenu(
                      e,
                      rowIndex,
                      colIndex,
                      col,
                      display,
                      canPopupEdit
                    )
            }
            sx={{
              ...columnCellSx(col),
              ...rowStickySx,

              zIndex:
                isFrozenRow && isFrozenColumn
                  ? 3
                  : isFrozenRow ||
                      isFrozenColumn
                    ? 2
                    : undefined,

              height:
                lineHeight *
                visualLineCount,

              /*
               * Normal rows keep normal table borders.
               *
               * Frozen rows use inset box-shadow instead.
               */
              borderBottom: isFrozenRow
                ? "none"
                : `1px solid ${gridLine}`,

              color:
                statusColor ||
                (isEditable
                  ? editableColor
                  : readOnlyColor),

              fontWeight: statusColor
                ? 800
                : undefined,

              verticalAlign: "middle",

              cursor: hasFeeAction
                ? "context-menu"
                : "cell",

              userSelect: "none",

              bgcolor: isInvalid
                ? isDark
                  ? "rgba(220,53,69,0.18)"
                  : "rgba(220,53,69,0.12)"
                : isHighlighted
                  ? highlightBg
                  : isEditingThisCell
                    ? isDark
                      ? "#1a323b"
                      : "#0d9489"
                    : isInRange
                      ? isDark
                        ? "#1a323b"
                        : "#0d9489"
                      : isRowLocked || lockedCols.has(colIndex)
                        ? lockedCellBg
                        : isFrozenRow ||
                            isFrozenColumn
                          ? isRowHovered
                            ? rowNumBg
                            : frozenCellBg
                          : isRowHovered
                            ? rowHoverBg
                            : isEditable
                              ? isDark
                                ? "rgba(20,184,166,0.04)"
                                : "rgba(13,148,136,0.03)"
                              : "transparent",

              /*
               * THIS is the main fix.
               *
               * Frozen rows get their bottom separator painted
               * inside their own sticky layer.
               */
              boxShadow: getCellBoxShadow({
                isInRange,
                isAnchorCell,
                isInvalid,
                isEditingThisCell,
              }),

              backgroundClip: isFrozenRow
                ? "padding-box"
                : undefined,

              transition:
                "background-color 0.4s ease",
            }}
            style={
              statusColor
                ? { color: statusColor }
                : undefined
            }
          >
            {isEditingThisCell ? (
              <Box
                component="input"
                autoFocus
                aria-label={`${col.label} row ${visualRow}`}
                value={display}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement>
                ) =>
                  onCellChange(
                    rowIndex,
                    col.id,
                    filterExcelInputByType(
                      col.type,
                      e.target.value
                    )
                  )
                }
                onBlur={(
                  e: React.FocusEvent<HTMLInputElement>
                ) =>
                  onCellBlur(
                    rowIndex,
                    col,
                    e.target.value
                  )
                }
                onFocus={(
                  e: React.FocusEvent<HTMLInputElement>
                ) => e.target.select()}
                onKeyDown={(
                  e: React.KeyboardEvent<HTMLInputElement>
                ) =>
                  onCellKeyDown(
                    e,
                    rowIndex,
                    col
                  )
                }
                sx={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "inherit",
                  font: "inherit",
                  textAlign:
                    col.align || "left",
                  p: 0,
                }}
              />
            ) : isPopupEdit ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  maxWidth: "100%",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {display}
                </Box>

                {canPopupEdit ? (
                  <EditOutlinedIcon
                    onClick={(e) => {
                      e.stopPropagation();

                      onPopupEditClick?.(
                        rowIndex,
                        col.id
                      );
                    }}
                    sx={{
                      fontSize: 13,
                      flexShrink: 0,
                      cursor: "pointer",
                      color: isDark
                        ? "rgba(226,232,240,0.45)"
                        : "rgba(15,23,42,0.35)",
                    }}
                  />
                ) : null}
              </Box>
            ) : col.alwaysShowFeeIcon &&
              hasFeeAction ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  maxWidth: "100%",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {display}
                </Box>

                <Tooltip
                  title={`Edit ${col.rightClickTooltip}`}
                  arrow
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();

                      onFeeEditDirect?.(
                        feeContext,
                        {
                          rowIndex,
                          row,
                          columnId: col.id,
                          label:
                            col.rightClickTooltip!,
                        }
                      );
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",
                      flexShrink: 0,
                      cursor: "pointer",
                      color: isDark
                        ? "rgba(226,232,240,0.45)"
                        : "rgba(15,23,42,0.35)",
                    }}
                  >
                    {col.feeIcon === "edit" ? (
                      <EditOutlinedIcon
                        sx={{
                          fontSize: 13,
                        }}
                      />
                    ) : (
                      <PaidOutlinedIcon
                        sx={{
                          fontSize: 13,
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>

                {col.secondaryAction ? (
                  <Tooltip
                    title={
                      col.secondaryAction
                        .tooltip
                    }
                    arrow
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();

                        onFeeEditDirect?.(
                          secondaryContext,
                          {
                            rowIndex,
                            row,
                            columnId: `${col.id}__secondary`,
                            label:
                              col
                                .secondaryAction!
                                .tooltip,
                          }
                        );
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          "center",
                        flexShrink: 0,
                        cursor: "pointer",
                        color: isDark
                          ? "rgba(226,232,240,0.45)"
                          : "rgba(15,23,42,0.35)",
                      }}
                    >
                      {col.secondaryAction
                        .icon === "edit" ? (
                        <EditOutlinedIcon
                          sx={{
                            fontSize: 13,
                          }}
                        />
                      ) : (
                        <PaidOutlinedIcon
                          sx={{
                            fontSize: 13,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                ) : null}
              </Box>
            ) : activeFeeCell &&
              activeFeeCell.rowIndex ===
                rowIndex &&
              activeFeeCell.columnId ===
                col.id &&
              activeFeeCell.lineIndex ===
                undefined ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onInlineFeeEditClick(e);
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                }}
              >
                <Tooltip
                  title={`Edit ${col.rightClickTooltip}`}
                  arrow
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        "center",
                      borderRadius: "50%",
                      bgcolor:
                        col.feeIcon === "edit"
                          ? editAccent
                          : accent,
                      color: "#fff",
                    }}
                  >
                    {col.feeIcon === "edit" ? (
                      <EditOutlinedIcon
                        sx={{
                          fontSize: 15,
                        }}
                      />
                    ) : (
                      <PaidOutlinedIcon
                        sx={{
                          fontSize: 15,
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>

                {display}
              </Box>
            ) : col.staticEditIcon &&
              hasValue ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  maxWidth: "100%",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {display}
                </Box>

                <EditOutlinedIcon
                  sx={{
                    fontSize: 13,
                    flexShrink: 0,
                    color: isDark
                      ? "rgba(226,232,240,0.45)"
                      : "rgba(15,23,42,0.35)",
                  }}
                />
              </Box>
            ) : (
              display
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
});

const ExcelSheetPopup: React.FC<ExcelSheetPopupProps> = ({
  open,
  onClose,
  title,
  subtitle,
  toolbar,
  headerActions,
  columns: columnsProp,
  rows = [],
  loading = false,
  emptyText = "No Invoice(s) Found",
  showExport = true,
  exportFileName = "export.csv",
  maxExportRecords,
  showTotals = false,
  totalsLabel,
  totalsValues,
  totalRecords,
  onLoadMore,
  loadMoreLoading = false,
  loadMoreSize = 100,
  onFetchAllRows,
  clearColumnFiltersSignal,
  reopenLoadingMs = 0,
  forceFullScreen = false,
  refreshing = false,
  highlightRowKey = null,
  onRowClick,
  onCellPopupEdit,
  getRowActions,
  onFeeEdit,
  getSelectionActions,
  onCellChange,
  savableColumnIds = [],
  onSaveRow,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const { tokens, paperSx, headerSx } = useModalStyles();
  const [columns, setColumns] = useState<ExcelSheetColumn[]>(columnsProp);
  useEffect(() => {
    setColumns(columnsProp);
  }, [columnsProp]);
  const dragColumnIdRef = useRef<string | null>(null);
  const dragOverColumnIdRef = useRef<string | null>(null);
  const dragSuppressClickRef = useRef(false);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);
  const [dragColumnOverlayRect, setDragColumnOverlayRect] = useState<{
    left: number;
    width: number;
    top: number;
    height: number;
  } | null>(null);
  const [dragTargetOverlayRect, setDragTargetOverlayRect] = useState<{
    left: number;
    width: number;
    top: number;
    height: number;
  } | null>(null);

  const getColumnOverlayRect = (headerEl: HTMLElement) => {
    const headerRect = headerEl.getBoundingClientRect();
    const containerRect = scrollRef.current?.getBoundingClientRect();
    return {
      left: headerRect.left,
      width: headerRect.width,
      top: containerRect?.top ?? headerRect.top,
      height: containerRect?.height ?? headerRect.height,
    };
  };

  const handleColumnHeaderMouseDown = (col: ExcelSheetColumn) => (event: React.MouseEvent) => {
    if (event.button !== 0) return;
    const headerEl = event.currentTarget as HTMLElement;
    const startX = event.clientX;
    const baseRect = getColumnOverlayRect(headerEl);
    const headerOwnRect = headerEl.getBoundingClientRect();
    const probeY = headerOwnRect.top + headerOwnRect.height / 2;
    let hasMoved = false;
    let rafId: number | null = null;
    let latestEvent: MouseEvent | null = null;

    const processMove = () => {
      rafId = null;
      const e = latestEvent;
      if (!e) return;
      const dx = e.clientX - startX;
      if (!hasMoved) {
        if (Math.abs(dx) < 4) return;
        hasMoved = true;
        dragSuppressClickRef.current = true;
        dragColumnIdRef.current = col.id;
        setDraggingColumnId(col.id);
      }
      setDragColumnOverlayRect({ ...baseRect, left: baseRect.left + dx });

      const hitEl = document.elementFromPoint(e.clientX, probeY) as HTMLElement | null;
      const targetHeaderEl = hitEl?.closest<HTMLElement>("[data-column-id]") ?? null;
      const targetColId = targetHeaderEl?.getAttribute("data-column-id") || null;
      const targetCol = targetColId ? columns.find((c) => c.id === targetColId) : undefined;
      if (targetCol && targetColId !== col.id && targetCol.group === col.group && targetHeaderEl) {
        if (dragOverColumnIdRef.current !== targetColId) {
          dragOverColumnIdRef.current = targetColId;
          setDragOverColumnId(targetColId);
          setDragTargetOverlayRect(getColumnOverlayRect(targetHeaderEl));
        }
      } else if (dragOverColumnIdRef.current !== null) {
        dragOverColumnIdRef.current = null;
        setDragOverColumnId(null);
        setDragTargetOverlayRect(null);
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      latestEvent = e;
      if (rafId === null) rafId = requestAnimationFrame(processMove);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      if (rafId !== null) cancelAnimationFrame(rafId);
      const targetId = dragOverColumnIdRef.current;
      dragColumnIdRef.current = null;
      dragOverColumnIdRef.current = null;
      setDraggingColumnId(null);
      setDragOverColumnId(null);
      setDragColumnOverlayRect(null);
      setDragTargetOverlayRect(null);
      if (hasMoved && targetId && targetId !== col.id) {
        setColumns((prev) => {
          const draggedIndex = prev.findIndex((c) => c.id === col.id);
          const targetIndex = prev.findIndex((c) => c.id === targetId);
          if (draggedIndex === -1 || targetIndex === -1) return prev;
          if (prev[draggedIndex].group !== prev[targetIndex].group) return prev;
          const next = [...prev];
          const [moved] = next.splice(draggedIndex, 1);
          next.splice(targetIndex, 0, moved);
          return next;
        });
      }
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const [editedRows, setEditedRows] = useState<ExcelSheetRow[] | null>(null);
  const [committedOverrides, setCommittedOverrides] = useState<Record<number, Record<string, unknown>>>({});
  const [savingRowIndexes, setSavingRowIndexes] = useState<Set<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  const [invalidCell, setInvalidCell] = useState<{ rowIndex: number; columnId: string; message: string } | null>(
    null
  );
  const [selectionAnchor, setSelectionAnchor] = useState<{ rowIndex: number; colIndex: number; lineIndex?: number } | null>(
    null
  );
  const [selectionFocus, setSelectionFocus] = useState<{ rowIndex: number; colIndex: number; lineIndex?: number } | null>(
    null
  );
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [sortState, setSortState] = useState<SortState | null>(null);
  const [colorSortState, setColorSortState] = useState<{ columnId: string; priorityStatus: string } | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, Set<string>>>({});
  useEffect(() => {
    setColumnFilters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearColumnFiltersSignal]);
  const [isExporting, setIsExporting] = useState(false);
  const [filterMenu, setFilterMenu] = useState<{ columnId: string; anchorEl: HTMLElement } | null>(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterDraft, setFilterDraft] = useState<Set<string> | null>(null);
  const [colorSortSubmenuAnchor, setColorSortSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [rowActionsMenu, setRowActionsMenu] = useState<{ mouseX: number; mouseY: number; rowIndex: number } | null>(
    null
  );
  const [feeTooltip, setFeeTooltip] = useState<{
    mouseX: number;
    mouseY: number;
    label: string;
    context: unknown;
    rowIndex: number;
    columnId: string;
    amount: string;
    secondary?: { label: string; context: unknown; icon?: "paid" | "edit" };
  } | null>(null);
  const [activeFeeCell, setActiveFeeCell] = useState<{
    label: string;
    context: unknown;
    rowIndex: number;
    columnId: string;
    lineIndex?: number;
    secondary?: { label: string; context: unknown; icon?: "paid" | "edit" };
  } | null>(null);
  const [cellContextMenu, setCellContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    rowIndex: number;
    colIndex: number;
    columnId: string;
    subRow?: Record<string, unknown>;
    copyText: string;
    canEdit: boolean;
  } | null>(null);
  const editStartValueRef = useRef<{ rowIndex: number; columnId: string; value: unknown } | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(DEFAULT_VIEWPORT);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pendingScrollIndexRef = useRef<number | null>(null);
  const wasOpenRef = useRef(false);
  const hasOpenedOnceRef = useRef(false);
  const prevOpenRef = useRef(false);
  const reopenStartedAtRef = useRef<number | null>(null);
  const [, setReopenTick] = useState(0);
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const [rowLineHeights, setRowLineHeights] = useState<Record<number, number>>({});
  const [frozenColumnId, setFrozenColumnId] = useState<string | null>(null);
  const [frozenRowCount, setFrozenRowCount] = useState<number>(0);
  const [freezeDragEnabled, setFreezeDragEnabled] = useState(false);
  const [freezeMenuAnchor, setFreezeMenuAnchor] = useState<HTMLElement | null>(null);
  const [freezeRowSubmenuAnchor, setFreezeRowSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [freezeColumnSubmenuAnchor, setFreezeColumnSubmenuAnchor] = useState<HTMLElement | null>(null);
  const [freezeRowInput, setFreezeRowInput] = useState("");
  const [rowFreezeDragLineY, setRowFreezeDragLineY] = useState<number | null>(null);
  const [colFreezeDragLineX, setColFreezeDragLineX] = useState<number | null>(null);
  const [selectedRowForFreeze, setSelectedRowForFreeze] = useState<number | null>(null);
  const [selectedColForFreeze, setSelectedColForFreeze] = useState<number | null>(null);
  const [lockedRows, setLockedRows] = useState<Set<number>>(new Set());
  const [lockedCols, setLockedCols] = useState<Set<number>>(new Set());
  /** Cells picked one by one with Ctrl/Cmd+click, keyed "rowIndex:colIndex". */
  const [pickedCells, setPickedCells] = useState<Set<string>>(new Set());
  const pickedColsByRow = useMemo(() => {
    const map = new Map<number, Set<number>>();
    pickedCells.forEach((key) => {
      const [r, c] = key.split(":").map(Number);
      if (!map.has(r)) map.set(r, new Set());
      map.get(r)!.add(c);
    });
    return map;
  }, [pickedCells]);

  useEffect(() => {
    setEditedRows(null);
    setCommittedOverrides({});
    setSavingRowIndexes(new Set());
    setRowErrors({});
    setEditingCell(null);
    setSelectionAnchor(null);
    setSelectionFocus(null);
    setIsSelectingRange(false);
    setInvalidCell(null);
    setFeeTooltip(null);
    setActiveFeeCell(null);
    setRowLineHeights({});
  }, [rows]);

  useEffect(() => {
    if (!invalidCell) return undefined;
    const timer = window.setTimeout(() => setInvalidCell(null), 1800);
    return () => window.clearTimeout(timer);
  }, [invalidCell]);

  useEffect(() => {
    if (!open) {
      setIsMaximized(false);
      setEditingCell(null);
      setSelectionAnchor(null);
      setSelectionFocus(null);
      setIsSelectingRange(false);
      setFeeTooltip(null);
      setActiveFeeCell(null);
      setLockedRows(new Set());
      setLockedCols(new Set());
      setPickedCells(new Set());
    }
  }, [open]);

  const selectionBounds = useMemo(() => {
    if (!selectionAnchor || !selectionFocus) return null;
    const sameCell =
      selectionAnchor.rowIndex === selectionFocus.rowIndex && selectionAnchor.colIndex === selectionFocus.colIndex;
    const hasLineIndex = selectionAnchor.lineIndex !== undefined && selectionFocus.lineIndex !== undefined;
    return {
      minRow: Math.min(selectionAnchor.rowIndex, selectionFocus.rowIndex),
      maxRow: Math.max(selectionAnchor.rowIndex, selectionFocus.rowIndex),
      minCol: Math.min(selectionAnchor.colIndex, selectionFocus.colIndex),
      maxCol: Math.max(selectionAnchor.colIndex, selectionFocus.colIndex),
      ...(sameCell && hasLineIndex
        ? {
            lineMin: Math.min(selectionAnchor.lineIndex!, selectionFocus.lineIndex!),
            lineMax: Math.max(selectionAnchor.lineIndex!, selectionFocus.lineIndex!),
          }
        : {}),
    };
  }, [selectionAnchor, selectionFocus]);


  if (prevOpenRef.current !== open) {
    if (open) {
      if (hasOpenedOnceRef.current && reopenLoadingMs > 0) {
        reopenStartedAtRef.current = Date.now();
      }
      hasOpenedOnceRef.current = true;
    } else {
      reopenStartedAtRef.current = null;
    }
    prevOpenRef.current = open;
  }

  const reopenLoading =
    reopenStartedAtRef.current != null &&
    Date.now() - reopenStartedAtRef.current < reopenLoadingMs;

  useEffect(() => {
    if (!reopenLoading || reopenStartedAtRef.current == null) return undefined;
    const remaining = reopenLoadingMs - (Date.now() - reopenStartedAtRef.current);
    const timer = window.setTimeout(() => {
      reopenStartedAtRef.current = null;
      setReopenTick((n) => n + 1);
    }, Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [open, reopenLoading, reopenLoadingMs]);

  const sheetRows = editedRows ?? rows ?? [];
  const isFullScreen = isMobile || isMaximized || forceFullScreen;

  const accent = tokens.accent;
  const loadingPulse = keyframes`
    0%, 100% { opacity: 0.55; transform: scale(0.97); }
    50% { opacity: 1; transform: scale(1); }
  `;
  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  `;
  const editAccent = isDark ? "#818cf8" : "#4f46e5";
  const gridLine = isDark ? "#262e3e" : "rgba(15,23,42,0.12)";
  const sheetBg = isDark ? "#0f1218" : "#ffffff";
  const letterBg = isDark ? "#1c2230" : "#eef2f6";
  const rowNumBg = isDark ? "#161b24" : "#f1f5f9";
  const headerLabelColor = isDark ? "#2dd4bf" : "#0d9488";
  const readOnlyColor = isDark ? "rgba(226,232,240,0.78)" : "#334155";
  const editableColor = isDark ? "#f8fafc" : "#0f172a";
  const lockedCellBg = isDark ? "rgba(7, 49, 63, 0.6)" : "rgba(51,65,85,0.28)";
  const lockedIndicatorBg = isDark ? "rgba(51,65,85,0.75)" : "rgba(7, 49, 63, 0.6)";
  const headerSectionDividerColor = isDark ? "#000000" : "#94a3b8";

  const totals = useMemo(() => {
    const sums: Record<string, string> = {};
    columns.forEach((col) => {
      if (col.total) {
        const prefix = col.type === "currency" && col.showCurrencySymbol ? "$" : "";
        const value = sumExcelColumn(sheetRows, col.id, columns);
        const formatted =
          col.type === "currency"
            ? formatExcelCurrency(value)
            : value.toLocaleString("en-US", { maximumFractionDigits: 0 });
        sums[col.id] = `${prefix}${formatted}`;
      }
    });
    return sums;
  }, [columns, sheetRows]);

  const loadedCount = sheetRows.length;
  const allCount = totalRecords ?? loadedCount;
  const remaining = Math.max(0, allCount - loadedCount);
  const nextBatch = Math.min(loadMoreSize, remaining);
  const hasMore = !!onLoadMore && remaining > 0;
  const useLoadMoreFooter = totalRecords != null || !!onLoadMore;
  const isLoadingNow = loading || reopenLoading;
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [localShowLoading, setLocalShowLoading] = useState(isLoadingNow);
  const [isTableReveal, startTableReveal] = useTransition();
  const showLoading = localShowLoading || isTableReveal;
  const showFooter = !showLoading && loadedCount > 0 && useLoadMoreFooter;
  const isCompact = !isFullScreen && (showLoading || loadedCount === 0);

  useEffect(() => {
    if (isLoadingNow) {
      setLocalShowLoading(true);
      setLoadingProgress(8);
      const interval = setInterval(() => {
        setLoadingProgress((prev) => {
          if (prev >= 98.5) return prev;
          return prev + (99 - prev) * 0.12;
        });
      }, 180);
      return () => clearInterval(interval);
    }
    setLoadingProgress((prev) => (prev === 0 ? 0 : 100));
    startTableReveal(() => setLocalShowLoading(false));
    return undefined;
  }, [isLoadingNow]);

  const getRowLineHeight = useCallback(
    // Rounded to a whole pixel — a fractional row height (e.g. from a manual row-resize
    // drag) can make a sticky frozen row's height flicker by a pixel during scroll repaints.
    (rowIndex: number) => Math.round(rowLineHeights[rowIndex] ?? BODY_ROW_HEIGHT),
    [rowLineHeights]
  );
  const sortedRowIndices = useMemo(() => {
    let indices = sheetRows.map((_, i) => i);
    const activeFilters = Object.entries(columnFilters).filter(([, allowed]) => allowed.size > 0);
    if (activeFilters.length > 0) {
      indices = indices.filter((i) =>
        activeFilters.every(([columnId, allowed]) => allowed.has(getColumnFilterKey(sheetRows[i], columnId)))
      );
    }
    if (colorSortState) {
      const col = columns.find((c) => c.id === colorSortState.columnId && !c.subRowsFrom && c.colorFrom);
      if (col) {
        indices = [...indices].sort((a, b) => {
          const ra = String(sheetRows[a][col.colorFrom!] ?? "").trim().toUpperCase() === colorSortState.priorityStatus ? 0 : 1;
          const rb = String(sheetRows[b][col.colorFrom!] ?? "").trim().toUpperCase() === colorSortState.priorityStatus ? 0 : 1;
          return ra - rb;
        });
      }
    } else if (sortState) {
      const col = columns.find((c) => c.id === sortState.columnId && !c.subRowsFrom);
      if (col) {
        const dir = sortState.direction === "asc" ? 1 : -1;
        indices = [...indices].sort((a, b) => {
          const va = getColumnSortValue(sheetRows[a], col);
          const vb = getColumnSortValue(sheetRows[b], col);
          if (va < vb) return -1 * dir;
          if (va > vb) return 1 * dir;
          return 0;
        });
      }
    }
    return indices;
  }, [sheetRows, sortState, colorSortState, columnFilters, columns]);
  const invoiceHeights = useMemo(
    () =>
      sortedRowIndices.map(
        (originalIndex) => getRowLineHeight(originalIndex) * invoiceVisualCount(sheetRows[originalIndex], columns)
      ),
    [sortedRowIndices, sheetRows, columns, getRowLineHeight]
  );
  const invoiceOffsets = useMemo(() => {
    const offsets = [0];
    invoiceHeights.forEach((height) => offsets.push(offsets[offsets.length - 1] + height));
    return offsets;
  }, [invoiceHeights]);
  const excelRowStarts = useMemo(() => sortedRowIndices.map((_, i) => i + 1), [sortedRowIndices.length]);

  const visibleRange = useMemo(() => {
    const count = sheetRows.length;
    if (count === 0) return { start: 0, end: 0 };
    const startY = Math.max(0, scrollTop - OVERSCAN_PX);
    const endY = scrollTop + viewportHeight + OVERSCAN_PX;
    let start = 0;
    while (start < count - 1 && invoiceOffsets[start + 1] <= startY) start += 1;
    let end = start;
    while (end < count && invoiceOffsets[end] < endY) end += 1;
    return { start, end: Math.min(count, Math.max(end, start + 1)) };
  }, [sheetRows.length, invoiceOffsets, scrollTop, viewportHeight]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) {
      setScrollTop(0);
      return undefined;
    }
    if (justOpened) {
      setScrollTop(0);
      if (el) el.scrollTop = 0;
    }
    if (!el) return undefined;
    const measure = () => {
      setViewportHeight(el.clientHeight || DEFAULT_VIEWPORT);
      setScrollTop(el.scrollTop);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, isCompact, showLoading]);

  useLayoutEffect(() => {
    const idx = pendingScrollIndexRef.current;
    if (idx == null || sheetRows.length <= idx) return;
    pendingScrollIndexRef.current = null;
    scrollRef.current?.scrollTo({
      top: invoiceOffsets[idx] || 0,
      behavior: "smooth",
    });
  }, [sheetRows.length, invoiceOffsets]);

  useEffect(() => {
    if (
      !loadMoreLoading &&
      pendingScrollIndexRef.current != null &&
      sheetRows.length <= pendingScrollIndexRef.current
    ) {
      pendingScrollIndexRef.current = null;
    }
  }, [loadMoreLoading, sheetRows.length]);

  const handleCellChange = (rowIndex: number, columnId: string, value: string) => {
    setEditedRows((prev) => {
      const next = [...(prev ?? rows ?? [])];
      next[rowIndex] = { ...next[rowIndex], [columnId]: value };
      return next;
    });
    onCellChange?.(rowIndex, columnId, value);
  };

  const handleCellBlur = (
    rowIndex: number,
    column: ExcelSheetColumn,
    value: string
  ) => {
    const isNumberType = column.type === "currency" || column.type === "number";
    const isInvalid =
      (isNumberType && !isValidExcelNumber(value)) || (column.type === "date" && !isValidExcelDate(value));
    if (isInvalid) {
      const start = editStartValueRef.current;
      const revertTo =
        start && start.rowIndex === rowIndex && start.columnId === column.id ? String(start.value ?? "") : "";
      handleCellChange(rowIndex, column.id, revertTo);
      setInvalidCell({
        rowIndex,
        columnId: column.id,
        message: isNumberType ? "Enter a valid number" : "Enter a valid date (MM/DD/YYYY)",
      });
      return;
    }
    let nextValue = value;
    if (column.type === "currency") nextValue = formatExcelCurrency(value);
    if (column.type === "date") nextValue = formatExcelDate(value);
    if (nextValue !== value) {
      handleCellChange(rowIndex, column.id, nextValue);
    }
  };

  const rowBaseline = (rowIndex: number): ExcelSheetRow => ({
    ...(rows[rowIndex] || {}),
    ...(committedOverrides[rowIndex] || {}),
  });

  const isRowDirty = (rowIndex: number): boolean => {
    if (savableColumnIds.length === 0) return false;
    const editedRow = sheetRows[rowIndex];
    if (!editedRow) return false;
    const baseline = rowBaseline(rowIndex);
    return savableColumnIds.some(
      (id) => normalizeForCompare(editedRow[id]) !== normalizeForCompare(baseline[id])
    );
  };

  const handleCancelRow = (rowIndex: number) => {
    const baseline = rowBaseline(rowIndex);
    setEditedRows((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      const reverted = { ...next[rowIndex] };
      savableColumnIds.forEach((id) => {
        reverted[id] = baseline[id];
      });
      next[rowIndex] = reverted;
      return next;
    });
    setRowErrors((prev) => {
      if (!(rowIndex in prev)) return prev;
      const next = { ...prev };
      delete next[rowIndex];
      return next;
    });
  };

  const handleSaveRow = async (rowIndex: number) => {
    if (!onSaveRow || savingRowIndexes.has(rowIndex)) return;
    const editedRow = sheetRows[rowIndex];
    const baseline = rowBaseline(rowIndex);
    const changedFields: Record<string, unknown> = {};
    savableColumnIds.forEach((id) => {
      if (normalizeForCompare(editedRow?.[id]) !== normalizeForCompare(baseline[id])) {
        changedFields[id] = editedRow?.[id];
      }
    });
    if (Object.keys(changedFields).length === 0) return;

    setSavingRowIndexes((prev) => new Set(prev).add(rowIndex));
    setRowErrors((prev) => {
      if (!(rowIndex in prev)) return prev;
      const next = { ...prev };
      delete next[rowIndex];
      return next;
    });
    try {
      await onSaveRow({ rowIndex, row: editedRow, original: rows[rowIndex], changedFields });
      setCommittedOverrides((prev) => ({
        ...prev,
        [rowIndex]: { ...(prev[rowIndex] || {}), ...changedFields },
      }));
    } catch (error: any) {
      setRowErrors((prev) => ({ ...prev, [rowIndex]: error?.message || "Save failed" }));
    } finally {
      setSavingRowIndexes((prev) => {
        const next = new Set(prev);
        next.delete(rowIndex);
        return next;
      });
    }
  };

  const handleCellInputBlur = (rowIndex: number, column: ExcelSheetColumn, value: string) => {
    handleCellBlur(rowIndex, column, value);
    setEditingCell((prev) =>
      prev && prev.rowIndex === rowIndex && prev.columnId === column.id ? null : prev
    );
  };

  const buildSelectionRangeText = (bounds: NonNullable<typeof selectionBounds>): string => {
    const lines: string[] = [];
    for (let r = bounds.minRow; r <= bounds.maxRow; r++) {
      const dataRow = sheetRows[r];
      const cells: string[] = [];
      for (let c = bounds.minCol; c <= bounds.maxCol; c++) {
        const col = columns[c];
        if (!col || !dataRow) {
          cells.push("");
          continue;
        }
        const raw = col.subRowsFrom ? getExcelSubRows(dataRow, columns)[0]?.[col.id] : dataRow[col.id];
        cells.push(raw === null || raw === undefined ? "" : String(raw));
      }
      lines.push(cells.join("\t"));
    }
    return lines.join("\n");
  };

  const closeCellContextMenu = () => setCellContextMenu(null);

  const handleCellContextMenu = (
    event: React.MouseEvent,
    rowIndex: number,
    colIndex: number,
    col: ExcelSheetColumn,
    cellDisplayValue: string,
    canEdit: boolean,
    subRow?: Record<string, unknown>,
    lineIndex?: number
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const inSelection =
      !!selectionBounds &&
      rowIndex >= selectionBounds.minRow &&
      rowIndex <= selectionBounds.maxRow &&
      colIndex >= selectionBounds.minCol &&
      colIndex <= selectionBounds.maxCol;
    const isPicked = pickedCells.has(`${rowIndex}:${colIndex}`);
    const copyText = inSelection ? buildSelectionRangeText(selectionBounds!) : cellDisplayValue;
    if (!inSelection && !isPicked) {
      setSelectionAnchor({ rowIndex, colIndex, lineIndex });
      setSelectionFocus({ rowIndex, colIndex, lineIndex });
    }
    setFeeTooltip(null);
    setCellContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      rowIndex,
      colIndex,
      columnId: col.id,
      subRow,
      copyText,
      canEdit,
    });
  };

  const handleCopyFromContextMenu = () => {
    if (cellContextMenu) {
      navigator.clipboard?.writeText(cellContextMenu.copyText).catch(() => {});
    }
    closeCellContextMenu();
  };

  const handleEditFromContextMenu = () => {
    const target = cellContextMenu;
    closeCellContextMenu();
    if (!target || !target.canEdit) return;
    onCellPopupEdit?.(sheetRows[target.rowIndex], target.rowIndex, target.columnId, target.subRow);
  };

  /** The cell whose right-click menu is open — a plain cell menu or a fee cell menu. */
  const contextMenuTarget: { rowIndex: number; colIndex: number } | null = cellContextMenu
    ? { rowIndex: cellContextMenu.rowIndex, colIndex: cellContextMenu.colIndex }
    : feeTooltip
      ? { rowIndex: feeTooltip.rowIndex, colIndex: columns.findIndex((c) => c.id === feeTooltip.columnId) }
      : null;

  const closeContextMenus = () => {
    closeCellContextMenu();
    setFeeTooltip(null);
  };

  /** Ctrl/Cmd+picked cells, when the open menu belongs to one of them. */
  const pickedForMenu = (): Array<{ rowIndex: number; colIndex: number }> =>
    contextMenuTarget && pickedCells.has(`${contextMenuTarget.rowIndex}:${contextMenuTarget.colIndex}`)
      ? Array.from(pickedCells).map((key) => {
          const [rowIndex, colIndex] = key.split(":").map(Number);
          return { rowIndex, colIndex };
        })
      : [];

  const contextMenuRowRange = (): number[] => {
    if (!contextMenuTarget) return [];
    const picked = pickedForMenu();
    if (picked.length > 0) return Array.from(new Set(picked.map((p) => p.rowIndex))).sort((a, b) => a - b);
    const minRow = selectionBounds ? selectionBounds.minRow : contextMenuTarget.rowIndex;
    const maxRow = selectionBounds ? selectionBounds.maxRow : contextMenuTarget.rowIndex;
    const range: number[] = [];
    for (let r = minRow; r <= maxRow; r++) range.push(r);
    return range;
  };

  const contextMenuColRange = (): number[] => {
    if (!contextMenuTarget) return [];
    const picked = pickedForMenu();
    if (picked.length > 0) return Array.from(new Set(picked.map((p) => p.colIndex))).sort((a, b) => a - b);
    const minCol = selectionBounds ? selectionBounds.minCol : contextMenuTarget.colIndex;
    const maxCol = selectionBounds ? selectionBounds.maxCol : contextMenuTarget.colIndex;
    const range: number[] = [];
    for (let c = minCol; c <= maxCol; c++) range.push(c);
    return range;
  };

  const areAllRowsLocked = (range: number[]): boolean => range.length > 0 && range.every((r) => lockedRows.has(r));
  const areAllColsLocked = (range: number[]): boolean => range.length > 0 && range.every((c) => lockedCols.has(c));

  const handleToggleLockRows = () => {
    const range = contextMenuRowRange();
    const shouldUnlock = areAllRowsLocked(range);
    setLockedRows((prev) => {
      const next = new Set(prev);
      range.forEach((r) => (shouldUnlock ? next.delete(r) : next.add(r)));
      return next;
    });
    closeContextMenus();
  };

  const handleToggleLockCols = () => {
    const range = contextMenuColRange();
    const shouldUnlock = areAllColsLocked(range);
    setLockedCols((prev) => {
      const next = new Set(prev);
      range.forEach((c) => (shouldUnlock ? next.delete(c) : next.add(c)));
      return next;
    });
    closeContextMenus();
  };

  /** Clears every selected row/column, not just the ones in the current click's range —
   *  for resetting after selecting several scattered rows/columns one at a time. */
  const handleDeselectAllRows = () => {
    setLockedRows(new Set());
    closeContextMenus();
  };

  const handleDeselectAllCols = () => {
    setLockedCols(new Set());
    closeContextMenus();
  };

  const handleDeselectAll = () => {
    setLockedRows(new Set());
    setLockedCols(new Set());
    setPickedCells(new Set());
    closeContextMenus();
  };

  /** Cells the Select/Deselect Cell item acts on — like Select Row(s), the whole dragged range when the
   *  menu was opened inside it, the picked cells when opened on one of them, else just the clicked cell. */
  const contextMenuCellKeys = (): string[] => {
    if (!contextMenuTarget || contextMenuTarget.colIndex < 0) return [];
    const targetKey = `${contextMenuTarget.rowIndex}:${contextMenuTarget.colIndex}`;
    if (pickedCells.has(targetKey)) return Array.from(pickedCells);
    if (selectionBounds) {
      const keys: string[] = [];
      for (let r = selectionBounds.minRow; r <= selectionBounds.maxRow; r++) {
        for (let c = selectionBounds.minCol; c <= selectionBounds.maxCol; c++) keys.push(`${r}:${c}`);
      }
      if (keys.includes(targetKey)) return keys;
    }
    return [targetKey];
  };

  const menuCellKeys = contextMenuCellKeys();
  const isContextMenuCellPicked = menuCellKeys.length > 0 && menuCellKeys.every((key) => pickedCells.has(key));

  /** Menu equivalent of Ctrl/Cmd+click: add (or remove) the clicked cell or selected range to the picked cells. */
  const handleTogglePickCell = () => {
    if (menuCellKeys.length === 0) return;
    setPickedCells((prev) => {
      const next = new Set(prev);
      menuCellKeys.forEach((key) => (isContextMenuCellPicked ? next.delete(key) : next.add(key)));
      return next;
    });
    setSelectionAnchor(null);
    setSelectionFocus(null);
    closeContextMenus();
  };

  const handleClearPickedCells = () => {
    setPickedCells(new Set());
    closeContextMenus();
  };

  /** Every cell in the dragged range plus every cell of the selected (locked) rows/columns. */
  const collectSelectedCells = (): ExcelSheetSelectedCell[] => {
    const seen = new Set<string>();
    const cells: ExcelSheetSelectedCell[] = [];
    const add = (rowIndex: number, colIndex: number) => {
      const row = sheetRows[rowIndex];
      const col = columns[colIndex];
      const key = `${rowIndex}:${colIndex}`;
      if (!row || !col || seen.has(key)) return;
      seen.add(key);
      cells.push({ rowIndex, row, columnId: col.id });
    };
    const bounds =
      selectionBounds ??
      (contextMenuTarget
        ? {
            minRow: contextMenuTarget.rowIndex,
            maxRow: contextMenuTarget.rowIndex,
            minCol: contextMenuTarget.colIndex,
            maxCol: contextMenuTarget.colIndex,
          }
        : null);
    if (bounds) {
      for (let r = bounds.minRow; r <= bounds.maxRow; r++) {
        for (let c = bounds.minCol; c <= bounds.maxCol; c++) add(r, c);
      }
    }
    lockedRows.forEach((r) => columns.forEach((_, c) => add(r, c)));
    lockedCols.forEach((c) => sheetRows.forEach((_, r) => add(r, c)));
    // Every picked cell counts, even when the menu was opened on a cell that is not picked yet.
    pickedCells.forEach((key) => {
      const [rowIndex, colIndex] = key.split(":").map(Number);
      add(rowIndex, colIndex);
    });
    return cells;
  };

  const contextMenuSelectionActions: ExcelSheetSelectionAction[] =
    contextMenuTarget && getSelectionActions ? getSelectionActions(collectSelectedCells()) : [];

  const handleSelectionActionClick = (action: ExcelSheetSelectionAction) => {
    closeContextMenus();
    action.onClick();
  };

  const closeRowActionsMenu = () => setRowActionsMenu(null);

  const handleRowContextMenu = (event: React.MouseEvent, rowIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    setRowActionsMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, rowIndex });
  };

  const handleSelectRow = (rowIndex: number) => {
    setActiveFeeCell(null);
    setSelectionAnchor({ rowIndex, colIndex: 0 });
    setSelectionFocus({ rowIndex, colIndex: columns.length - 1 });
    setSelectedRowForFreeze(rowIndex);
  };

  const handleSelectColumn = (colIndex: number) => {
    setActiveFeeCell(null);
    setSelectionAnchor({ rowIndex: 0, colIndex });
    setSelectionFocus({ rowIndex: Math.max(0, sheetRows.length - 1), colIndex });
    setSelectedColForFreeze(colIndex);
  };

  const filterMenuItemSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 1,
    py: 0.75,
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.82rem",
    "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)" },
  } as const;
  const filterMenuCheckRowSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    px: 0.5,
    borderRadius: "8px",
    cursor: "pointer",
    "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)" },
  } as const;
  const filterMenuCheckboxSx = {
    "&.Mui-checked": { color: accent },
  } as const;

  const filterMenuColumn = filterMenu ? columns.find((c) => c.id === filterMenu.columnId) : undefined;

  const filterMenuRowsInScope = useMemo(() => {
    if (!filterMenu) return [];
    const otherFilters = Object.entries(columnFilters).filter(
      ([columnId, allowed]) => columnId !== filterMenu.columnId && allowed.size > 0
    );
    if (otherFilters.length === 0) return sheetRows;
    return sheetRows.filter((row) =>
      otherFilters.every(([columnId, allowed]) => allowed.has(getColumnFilterKey(row, columnId)))
    );
  }, [filterMenu, columnFilters, sheetRows]);

  const filterMenuOptions = useMemo(() => {
    if (!filterMenu || !filterMenuColumn) return [];
    const seen = new Map<string, string>();
    filterMenuRowsInScope.forEach((row) => {
      const key = getColumnFilterKey(row, filterMenu.columnId);
      if (!seen.has(key)) {
        const value = row[filterMenu.columnId];
        const label = key === FILTER_BLANK_KEY ? "(Blanks)" : formatCellValue(filterMenuColumn, value) || String(value);
        seen.set(key, label);
      }
    });
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => {
        if (a.key === FILTER_BLANK_KEY) return -1;
        if (b.key === FILTER_BLANK_KEY) return 1;
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      });
  }, [filterMenu, filterMenuColumn, filterMenuRowsInScope]);

  const filterMenuVisibleOptions = useMemo(
    () =>
      filterSearch.trim()
        ? filterMenuOptions.filter((opt) => opt.label.toLowerCase().includes(filterSearch.trim().toLowerCase()))
        : filterMenuOptions,
    [filterMenuOptions, filterSearch]
  );

  const handleOpenFilterMenu = (columnId: string, anchorEl: HTMLElement) => {
    const existing = columnFilters[columnId];
    setFilterSearch("");
    setFilterMenu({ columnId, anchorEl });
    setFilterDraft(existing && existing.size > 0 ? new Set(existing) : null);
  };

  const handleCloseFilterMenu = () => {
    setFilterMenu(null);
    setFilterDraft(null);
    setFilterSearch("");
    setColorSortSubmenuAnchor(null);
  };

  const handleSortFromMenu = (direction: "asc" | "desc") => {
    if (filterMenu) {
      const isSameSort = sortState?.columnId === filterMenu.columnId && sortState.direction === direction;
      setSortState(isSameSort ? null : { columnId: filterMenu.columnId, direction });
    }
    setColorSortState(null);
    setColumnFilters({});
    handleCloseFilterMenu();
  };

  const handleClearColorSortFromMenu = () => {
    setColorSortState(null);
    handleCloseFilterMenu();
  };

  const handleSortByColorFromMenu = (status: string) => {
    if (filterMenu) {
      const isSamePriority =
        colorSortState?.columnId === filterMenu.columnId && colorSortState.priorityStatus === status;
      setColorSortState(isSamePriority ? null : { columnId: filterMenu.columnId, priorityStatus: status });
    }
    setSortState(null);
    setColumnFilters({});
    handleCloseFilterMenu();
  };

  const handleClearFilterFromMenu = () => {
    if (filterMenu) {
      setColumnFilters((prev) => {
        const next = { ...prev };
        delete next[filterMenu.columnId];
        return next;
      });
    }
    handleCloseFilterMenu();
  };

  const isFilterOptionChecked = (key: string) => (filterDraft ? filterDraft.has(key) : true);

  const toggleFilterOption = (key: string) => {
    setFilterDraft((prev) => {
      const base = prev ? new Set(prev) : new Set(filterMenuOptions.map((o) => o.key));
      if (base.has(key)) base.delete(key);
      else base.add(key);
      return base;
    });
  };

  const isFilterSelectAllChecked =
    filterMenuVisibleOptions.length > 0 && filterMenuVisibleOptions.every((opt) => isFilterOptionChecked(opt.key));

  const toggleFilterSelectAll = () => {
    const shouldCheck = !isFilterSelectAllChecked;
    setFilterDraft((prev) => {
      const base = prev ? new Set(prev) : new Set(filterMenuOptions.map((o) => o.key));
      filterMenuVisibleOptions.forEach((opt) => {
        if (shouldCheck) base.add(opt.key);
        else base.delete(opt.key);
      });
      return base;
    });
  };

  const handleApplyFilterFromMenu = () => {
    if (!filterMenu) return;
    const selected =
      filterDraft ??
      new Set((filterSearch.trim() ? filterMenuVisibleOptions : filterMenuOptions).map((o) => o.key));
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (selected.size === 0 || selected.size >= filterMenuOptions.length) {
        delete next[filterMenu.columnId];
      } else {
        next[filterMenu.columnId] = selected;
      }
      return next;
    });
    handleCloseFilterMenu();
  };

  const handleCellMouseDown = (event: React.MouseEvent, rowIndex: number, colIndex: number, lineIndex?: number) => {
    if (event.button !== 0) return;
    closeCellContextMenu();
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd+click adds or removes one cell, keeping what is already selected (the
      // current dragged range is folded into the picked set so it stays selected too).
      event.preventDefault();
      setActiveFeeCell(null);
      setPickedCells((prev) => {
        const next = new Set(prev);
        if (selectionBounds) {
          for (let r = selectionBounds.minRow; r <= selectionBounds.maxRow; r++) {
            for (let c = selectionBounds.minCol; c <= selectionBounds.maxCol; c++) next.add(`${r}:${c}`);
          }
        }
        const key = `${rowIndex}:${colIndex}`;
        if (prev.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      setSelectionAnchor(null);
      setSelectionFocus(null);
      return;
    }
    if (pickedCells.size > 0) setPickedCells(new Set());
    // Leave the active fee cell alone when the mousedown lands back on it, so the
    // click that follows can still open the edit popup; clear it for any other cell.
    if (
      activeFeeCell &&
      (activeFeeCell.rowIndex !== rowIndex || activeFeeCell.columnId !== columns[colIndex]?.id)
    ) {
      setActiveFeeCell(null);
    }
    setSelectionAnchor({ rowIndex, colIndex, lineIndex });
    setSelectionFocus({ rowIndex, colIndex, lineIndex });
    setIsSelectingRange(true);
  };

  const handleCellMouseEnter = (rowIndex: number, colIndex: number, lineIndex?: number) => {
    if (!isSelectingRange) return;
    setSelectionFocus({ rowIndex, colIndex, lineIndex });
  };

  useEffect(() => {
    if (!isSelectingRange) return undefined;
    const handleMouseUp = () => setIsSelectingRange(false);
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [isSelectingRange]);

  useEffect(() => {
    if (!selectionBounds) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeTag = (document.activeElement as HTMLElement | null)?.tagName;
      if (activeTag === "INPUT" || activeTag === "TEXTAREA") return;
      const isCopyShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "c";
      if (!isCopyShortcut) return;
      event.preventDefault();
      const text = buildSelectionRangeText(selectionBounds);
      navigator.clipboard?.writeText(text).catch(() => {});
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectionBounds, sheetRows, columns]);

  const resolveCellContextMenuInfo = (
    rowIndex: number,
    colIndex: number,
    subIndex?: number
  ): {
    columnId: string;
    subRow?: Record<string, unknown>;
    copyText: string;
    canEdit: boolean;
  } | null => {
    const col = columns[colIndex];
    const row = sheetRows[rowIndex];
    if (!col || !row) return null;
    const isPopupEdit = !!col.popupEdit && !!onCellPopupEdit;
    if (col.subRowsFrom) {
      const subRows = getExcelSubRows(row, columns);
      const index = subIndex !== undefined && subRows[subIndex] ? subIndex : subRows.length - 1;
      const subRow = subRows[index];
      const value = subRow?.[col.id];
      const hasValue = value !== null && value !== undefined && value !== "";
      const dueAmount = col.dueField ? parseExcelNumber((row as any)[col.dueField]) : undefined;
      const hasDue = dueAmount !== undefined && dueAmount > 0.004;
      const isLastSubRow = index === subRows.length - 1;
      const canEdit =
        isPopupEdit &&
        (col.dueField
          ? hasDue && isLastSubRow
          : col.popupEditIconWhenEmpty
            ? !hasValue
            : hasValue);
      return {
        columnId: col.id,
        subRow,
        copyText: hasValue ? String(value) : "",
        canEdit,
      };
    }
    const value = row[col.id];
    const hasValue = value !== null && value !== undefined && value !== "";
    const canEdit = isPopupEdit && (col.popupEditIconWhenEmpty ? !hasValue : hasValue);
    return {
      columnId: col.id,
      copyText: hasValue ? String(value) : "",
      canEdit,
    };
  };

  useEffect(() => {
    if (!open) return undefined;
    // Fallback for a right-click that lands somewhere a cell's own onContextMenu
    // didn't handle (e.g. the open menu's invisible backdrop sitting on top of
    // another cell) — a per-cell handler already stopped propagation before this
    // fires, so this only runs for the leftover cases. Hit-test the real element
    // under the cursor so the menu still reflects that exact cell, not a stale one.
    const handleFallbackContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      const cellEl = document
        .elementsFromPoint(event.clientX, event.clientY)
        .find((el) => (el as HTMLElement).dataset?.rowIndex !== undefined) as HTMLElement | undefined;
      const rowIndex = cellEl ? Number(cellEl.dataset.rowIndex) : NaN;
      const colIndex = cellEl ? Number(cellEl.dataset.colIndex) : NaN;
      const subIndex = cellEl?.dataset.subIndex !== undefined ? Number(cellEl.dataset.subIndex) : undefined;
      const info = Number.isFinite(rowIndex) && Number.isFinite(colIndex)
        ? resolveCellContextMenuInfo(rowIndex, colIndex, subIndex)
        : null;
      if (!info) return;
      if (!pickedCells.has(`${rowIndex}:${colIndex}`)) {
        setSelectionAnchor({ rowIndex, colIndex });
        setSelectionFocus({ rowIndex, colIndex });
      }
      setFeeTooltip(null);
      setCellContextMenu({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
        rowIndex,
        colIndex,
        ...info,
      });
    };
    document.addEventListener("contextmenu", handleFallbackContextMenu);
    return () => document.removeEventListener("contextmenu", handleFallbackContextMenu);
  }, [open, columns, sheetRows, pickedCells]);

  const handleCellKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    rowIndex: number,
    column: ExcelSheetColumn
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      const start = editStartValueRef.current;
      if (start && start.rowIndex === rowIndex && start.columnId === column.id) {
        handleCellChange(rowIndex, column.id, String(start.value ?? ""));
      }
      setEditingCell(null);
    }
  };

  const handleFeeContextMenu = (
    event: React.MouseEvent,
    label: string,
    context: unknown,
    rowIndex: number,
    columnId: string,
    amount: string,
    secondary?: { label: string; context: unknown; icon?: "paid" | "edit" }
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveFeeCell(null);
    closeCellContextMenu();
    // Same as a plain cell: keep an existing selection that covers this cell, else select just this cell.
    const colIndex = columns.findIndex((c) => c.id === columnId);
    const inSelection =
      !!selectionBounds &&
      rowIndex >= selectionBounds.minRow &&
      rowIndex <= selectionBounds.maxRow &&
      colIndex >= selectionBounds.minCol &&
      colIndex <= selectionBounds.maxCol;
    const isPicked = pickedCells.has(`${rowIndex}:${colIndex}`);
    if (!inSelection && !isPicked && colIndex >= 0) {
      setSelectionAnchor({ rowIndex, colIndex });
      setSelectionFocus({ rowIndex, colIndex });
    }
    setFeeTooltip({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      label,
      context,
      rowIndex,
      columnId,
      amount,
      secondary,
    });
  };

  const closeFeeTooltip = () => setFeeTooltip(null);

  const handleFeeEditSecondaryClick = () => {
    if (feeTooltip?.secondary) {
      onFeeEdit?.(feeTooltip.secondary.context, {
        rowIndex: feeTooltip.rowIndex,
        row: sheetRows[feeTooltip.rowIndex],
        columnId: `${feeTooltip.columnId}__secondary`,
        label: feeTooltip.secondary.label,
      });
    }
    closeFeeTooltip();
  };

  const handleFeeClick = (
    event: React.MouseEvent,
    label: string,
    context: unknown,
    rowIndex: number,
    columnId: string,
    _amount: string,
    lineIndex?: number,
    secondary?: { label: string; context: unknown; icon?: "paid" | "edit" }
  ) => {
    // Ctrl/Cmd+click is picking cells for a multi-cell selection, not opening the fee's edit icons.
    if (event.ctrlKey || event.metaKey) return;
    setActiveFeeCell({ label, context, rowIndex, columnId, lineIndex, secondary });
  };

  const handleInlineFeeEditClick = () => {
    if (activeFeeCell) {
      onFeeEdit?.(activeFeeCell.context, {
        rowIndex: activeFeeCell.rowIndex,
        row: sheetRows[activeFeeCell.rowIndex],
        columnId: activeFeeCell.columnId,
        label: activeFeeCell.label,
      });
    }
    setActiveFeeCell(null);
  };

  const handleInlineFeeEditSecondaryClick = () => {
    if (activeFeeCell?.secondary) {
      onFeeEdit?.(activeFeeCell.secondary.context, {
        rowIndex: activeFeeCell.rowIndex,
        row: sheetRows[activeFeeCell.rowIndex],
        columnId: `${activeFeeCell.columnId}__secondary`,
        label: activeFeeCell.secondary.label,
      });
    }
    setActiveFeeCell(null);
  };

  const handleFeeEditClick = () => {
    if (feeTooltip) {
      onFeeEdit?.(feeTooltip.context, {
        rowIndex: feeTooltip.rowIndex,
        row: sheetRows[feeTooltip.rowIndex],
        columnId: feeTooltip.columnId,
        label: feeTooltip.label,
      });
    }
    closeFeeTooltip();
  };

  /** Select/Deselect Row & Column items — shared by the plain cell menu and the fee cell menu. */
  const selectionMenuItems = (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Box
            role="button"
            aria-label="Select or Deselect cell"
            onClick={handleTogglePickCell}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              fontSize: "0.82rem",
              cursor: "pointer",
              color: isDark ? "#f8fafc" : "#0f172a",
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
            }}
          >
            {isContextMenuCellPicked
              ? menuCellKeys.length > 1
                ? "Deselect Cells"
                : "Deselect Cell"
              : menuCellKeys.length > 1
                ? "Select Cells"
                : "Select Cell"}
          </Box>
          <Box
            role="button"
            aria-label="Select or Deselect row"
            onClick={handleToggleLockRows}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              fontSize: "0.82rem",
              cursor: "pointer",
              color: isDark ? "#f8fafc" : "#0f172a",
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
            }}
          >
            {areAllRowsLocked(contextMenuRowRange())
              ? contextMenuRowRange().length > 1
                ? "Deselect Rows"
                : "Deselect Row"
              : contextMenuRowRange().length > 1
                ? "Select Rows"
                : "Select Row"}
          </Box>
          <Box
            role="button"
            aria-label="Select or Deselect column"
            onClick={handleToggleLockCols}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              fontSize: "0.82rem",
              cursor: "pointer",
              color: isDark ? "#f8fafc" : "#0f172a",
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
            }}
          >
            {areAllColsLocked(contextMenuColRange())
              ? contextMenuColRange().length > 1
                ? "Deselect Columns"
                : "Deselect Column"
              : contextMenuColRange().length > 1
                ? "Select Columns"
                : "Select Column"}
          </Box>
          {lockedRows.size > 0 || lockedCols.size > 0 || pickedCells.size > 0 ? (
            <>
              <Divider sx={{ my: 0.5 }} />
              {pickedCells.size > 0 ? (
                <Box
                  role="button"
                  aria-label="Deselect all cells"
                  onClick={handleClearPickedCells}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    color: isDark ? "#f8fafc" : "#0f172a",
                    "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
                  }}
                >
                  Deselect All Cells ({pickedCells.size})
                </Box>
              ) : null}
              {lockedRows.size > 0 ? (
                <Box
                  role="button"
                  aria-label="Deselect all rows"
                  onClick={handleDeselectAllRows}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    color: isDark ? "#f8fafc" : "#0f172a",
                    "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
                  }}
                >
                  Deselect All Rows
                </Box>
              ) : null}
              {lockedCols.size > 0 ? (
                <Box
                  role="button"
                  aria-label="Deselect all columns"
                  onClick={handleDeselectAllCols}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    color: isDark ? "#f8fafc" : "#0f172a",
                    "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
                  }}
                >
                  Deselect All Columns
                </Box>
              ) : null}
              {lockedRows.size > 0 && lockedCols.size > 0 ? (
                <Box
                  role="button"
                  aria-label="Deselect all rows and columns"
                  onClick={handleDeselectAll}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    color: isDark ? "#f8fafc" : "#0f172a",
                    "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
                  }}
                >
                  Deselect All Rows &amp; Columns
                </Box>
              ) : null}
            </>
          ) : null}
        </>
  );

  const selectionMenuActionItems =
    contextMenuSelectionActions.length > 0 ? (
      <>
        <Divider sx={{ my: 0.5 }} />
        {contextMenuSelectionActions.map((action) => (
          <Box
            key={action.key}
            role="button"
            aria-label={action.label}
            onClick={() => handleSelectionActionClick(action)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              fontSize: "0.82rem",
              fontWeight: 700,
              cursor: "pointer",
              color: isDark ? "#f8fafc" : "#0f172a",
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
            }}
          >
            {action.icon ? (
              <Box sx={{ display: "flex", alignItems: "center", color: action.iconColor || accent }}>{action.icon}</Box>
            ) : null}
            {action.label}
          </Box>
        ))}
      </>
    ) : null;

  const exportOverLimit = maxExportRecords != null && allCount > maxExportRecords;
  const exportLimitMessage =
    maxExportRecords != null
      ? `Export is available only when the list contains ${maxExportRecords.toLocaleString("en-US")} records or fewer. Maximum export size: ${maxExportRecords} records.`
      : "";

  const handleExport = async () => {
    if (exportOverLimit) return;
    setIsExporting(true);
    try {
      const needsFullFetch = !!onFetchAllRows && allCount > sheetRows.length;
      const baseRows = needsFullFetch ? await onFetchAllRows() : sheetRows;
      const activeFilters = Object.entries(columnFilters).filter(([, allowed]) => allowed.size > 0);
      const filteredRows =
        activeFilters.length > 0
          ? baseRows.filter((row) =>
              activeFilters.every(([columnId, allowed]) => allowed.has(getColumnFilterKey(row, columnId)))
            )
          : baseRows;
      const rowsForExport =
        maxExportRecords != null ? filteredRows.slice(0, maxExportRecords) : filteredRows;
      const csv = buildExcelCsv(columns, rowsForExport, showTotals);
      downloadCsv(csv, exportFileName);
    } finally {
      setIsExporting(false);
    }
  };

  const toolbarStackBtnSx = {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "1px",
    px: 0.75,
    py: 0.5,
    minWidth: 52,
    borderRadius: 1,
    cursor: "pointer",
    userSelect: "none" as const,
    "&:hover": {
      bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
    },
  };

  const isToolbarRenderProp = typeof toolbar === "function";
  const exportDisabled = showLoading || isExporting || sheetRows.length === 0 || exportOverLimit;
  const exportButtonNode = showExport ? (
    <Tooltip
      title={exportOverLimit ? exportLimitMessage : ""}
      arrow
      placement="bottom"
      disableHoverListener={!exportOverLimit}
      disableFocusListener={!exportOverLimit}
      disableTouchListener={!exportOverLimit}
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Box
          component="button"
          type="button"
          onClick={handleExport}
          disabled={exportDisabled}
          aria-label="Export CSV"
          sx={{
            ...toolbarStackBtnSx,
            border: "none",
            background: "none",
            color: tokens.textSecondary,
            cursor: exportDisabled ? "not-allowed" : "pointer",
            opacity: exportDisabled ? 0.5 : 1,
          }}
        >
          {isExporting ? (
            <CircularProgress size={20} sx={{ color: "inherit" }} />
          ) : (
            <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
          )}
          <Box sx={{ fontSize: 11, lineHeight: 1.15, textAlign: "center" }}>Export</Box>
          <Box sx={{ fontSize: 11, lineHeight: 1.15, textAlign: "center" }}>CSV</Box>
        </Box>
      </Box>
    </Tooltip>
  ) : null;

  const selectedDisplayRowForFreeze = selectedRowForFreeze !== null ? sortedRowIndices.indexOf(selectedRowForFreeze) : -1;
  const selectedColIndexForFreeze = selectedColForFreeze !== null ? selectedColForFreeze : -1;
  const selectedExcelRowLabel = selectedDisplayRowForFreeze >= 0 ? excelRowStarts[selectedDisplayRowForFreeze] : null;
  const selectedColLetterLabel =
    selectedColIndexForFreeze >= 0 && columns[selectedColIndexForFreeze]
      ? columns[selectedColIndexForFreeze].label || toColLetters(selectedColIndexForFreeze)
      : null;

  /** Matches Excel: freezes rows above and columns left of the selected cell (the
   *  cell's own row/column stay scrollable) — uses the normal drag/click cell
   *  selection, not the whole-row/whole-column "select row"/"select column" click. */
  const handleFreezeAtSelection = () => {
    if (selectionBounds) {
      const displayRow = sortedRowIndices.indexOf(selectionBounds.minRow);
      if (displayRow >= 0) setFrozenRowCount(displayRow);
      const colIdx = selectionBounds.minCol;
      setFrozenColumnId(colIdx > 0 && columns[colIdx - 1] ? columns[colIdx - 1].id : null);
    }
    setFreezeDragEnabled(true);
    setFreezeMenuAnchor(null);
  };
  const handleFreezeTopRow = () => {
    setFrozenRowCount(1);
    setFreezeDragEnabled(false);
    setFreezeMenuAnchor(null);
  };
  const handleOpenFreezeRowSubmenu = (e: React.MouseEvent<HTMLElement>) => {
    setFreezeRowInput(selectedExcelRowLabel !== null ? String(selectedExcelRowLabel) : "1");
    setFreezeRowSubmenuAnchor(e.currentTarget);
  };
  const handleApplyFreezeRowInput = () => {
    const excelRow = Number(freezeRowInput);
    if (Number.isFinite(excelRow) && excelRow >= 1) {
      const displayPosition = excelRow - 1;
      setFrozenRowCount(Math.min(displayPosition + 1, sortedRowIndices.length));
    }
    setFreezeDragEnabled(false);
    setFreezeRowSubmenuAnchor(null);
    setFreezeMenuAnchor(null);
  };
  const handleFreezeFirstColumn = () => {
    if (columns.length > 0) setFrozenColumnId(columns[0].id);
    setFreezeDragEnabled(false);
    setFreezeMenuAnchor(null);
  };
  const handlePickFreezeColumn = (colId: string) => {
    setFrozenColumnId(colId);
    setFreezeDragEnabled(false);
    setFreezeColumnSubmenuAnchor(null);
    setFreezeMenuAnchor(null);
  };
  const handleUnfreezePanes = () => {
    setFrozenRowCount(0);
    setFrozenColumnId(null);
    setFreezeDragEnabled(false);
    setFreezeMenuAnchor(null);
  };

  const handleToggleBrowserFullscreen = async () => {
  try {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  } catch (error) {
    console.error("Fullscreen toggle failed:", error);
  }
};

  useEffect(() => {
  const handleFullscreenChange = () => {
    setIsBrowserFullscreen(!!document.fullscreenElement);
  };

  document.addEventListener("fullscreenchange", handleFullscreenChange);

  return () => {
    document.removeEventListener("fullscreenchange", handleFullscreenChange);
  };
}, []);

  useEffect(() => {
    if (!open && document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
  }, [open]);

  const controlsButtonsNode = (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Freeze panes"
        onClick={(e) => setFreezeMenuAnchor(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setFreezeMenuAnchor(e.currentTarget);
        }}
        sx={{
          ...toolbarStackBtnSx,
          color: frozenColumnId || frozenRowCount > 0
            ? accent
            : tokens.textSecondary,
        }}
      >
        <AcUnitIcon sx={{ fontSize: 20 }} />
        <Box sx={{ fontSize: 11, lineHeight: 1.15, textAlign: "center" }}>Freeze</Box>
        <Box sx={{ display: "flex", alignItems: "center", fontSize: 11, lineHeight: 1.15 }}>
          Panes
          <ArrowDropDownIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
      {!isMobile && !forceFullScreen ? (
        <IconButton
          aria-label={isMaximized ? "Minimize" : "Maximize"}
          onClick={() => setIsMaximized((prev) => !prev)}
          size="small"
          sx={{ color: tokens.textSecondary }}
        >
          {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      ) : null}
      <Box
        role="button"
        tabIndex={0}
        aria-label={isBrowserFullscreen ? "Exit full screen" : "Full screen"}
        onClick={handleToggleBrowserFullscreen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleToggleBrowserFullscreen();
        }}
        sx={{ ...toolbarStackBtnSx, color: isBrowserFullscreen ? accent : tokens.textSecondary }}
      >
        {isBrowserFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
        <Box sx={{ fontSize: 11, lineHeight: 1.15, textAlign: "center" }}>
          {isBrowserFullscreen ? "Exit" : "Full"}
        </Box>
        <Box sx={{ fontSize: 11, lineHeight: 1.15, textAlign: "center" }}>Screen</Box>
      </Box>
      <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: tokens.textSecondary }}>
        <CloseIcon />
      </IconButton>
    </>
  );

  const handleLoadMore = () => {
    if (!onLoadMore || loadMoreLoading) return;
    pendingScrollIndexRef.current = sheetRows.length;
    onLoadMore();
  };

  const handleSheetScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
    setScrollLeft(event.currentTarget.scrollLeft);
  }, []);

  const handleColumnResizeMouseDown = (event: React.MouseEvent, col: ExcelSheetColumn) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = colWidths[col.id] ?? col.minWidth ?? 120;
    const onMouseMove = (e: MouseEvent) => {
      const nextWidth = Math.max(MIN_COL_WIDTH, startWidth + (e.clientX - startX));
      setColWidths((prev) => ({ ...prev, [col.id]: nextWidth }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleRowResizeMouseDown = (event: React.MouseEvent, rowIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    const startY = event.clientY;
    const startLineHeight = getRowLineHeight(rowIndex);
    const visualLines = invoiceVisualCount(sheetRows[rowIndex], columns);
    const onMouseMove = (e: MouseEvent) => {
      const delta = (e.clientY - startY) / visualLines;
      const nextLineHeight = Math.min(
        MAX_ROW_LINE_HEIGHT,
        Math.max(MIN_ROW_LINE_HEIGHT, startLineHeight + delta)
      );
      setRowLineHeights((prev) => ({ ...prev, [rowIndex]: nextLineHeight }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const hasEditableColumn = columns.some((col) => col.editable && !col.subRowsFrom);
  const INDEX_COL_WIDTH = savableColumnIds.length > 0 || hasEditableColumn || onRowClick ? 60 : 42;
  const columnWidth = (col: ExcelSheetColumn) => colWidths[col.id] ?? col.minWidth ?? 120;
  const frozenColumnIndex = frozenColumnId ? columns.findIndex((c) => c.id === frozenColumnId) : -1;
  const columnLeftOffsets = useMemo(() => {
    let running = INDEX_COL_WIDTH;
    return columns.map((c) => {
      const left = running;
      running += columnWidth(c);
      return left;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, colWidths, INDEX_COL_WIDTH]);
  const frozenCellBg = sheetBg;
  const columnGroups = useMemo(() => {
    const groups: { label: string; span: number; lastCol: ExcelSheetColumn }[] = [];
    columns.forEach((col) => {
      const last = groups[groups.length - 1];
      if (col.group && last && last.label === col.group) {
        last.span += 1;
        last.lastCol = col;
      } else {
        groups.push({ label: col.group || "", span: 1, lastCol: col });
      }
    });
    return groups;
  }, [columns]);
  const hasColumnGroups = columns.some((col) => col.group);
  /** Distinct color per column group — cycled in the order groups first appear, so
   *  the group header and its column names share a color unique from neighboring groups. */
  const groupHeaderColors = useMemo(() => {
    // Solid (non-transparent) fills — a sticky-positioned header cell with an alpha
    // background can show stale/ghosted content bleeding through from rows scrolling
    // underneath it, so every group color here is fully opaque.
    // `subBg` is a slightly muted variant of `bg` for the column-name row directly
    // below the group header, so the two rows read as one section without being
    // identical blocks of color.
    const palette = isDark
      ? [
          { bg: "#12211f", subBg: "#0c1917", text: "#5eead4" }, // teal
          { bg: "#1c1a30", subBg: "#151328", text: "#a5b4fc" }, // indigo
          { bg: "#1c1c1c", subBg: "#1c1c1c", text: "#e3fdcd" }, // violet
          { bg: "#2a1810", subBg: "#20120c", text: "#b1b1b1" }, // rust
          { bg: "#101f30", subBg: "#0b1626", text: "#93c5fd" }, // blue
          { bg: "#11201a", subBg: "#0c1813", text: "#86efac" }, // green
        ]
      : [
          { bg: "#d7e6e3", subBg: "#eaf3f1", text: "#0d9488" }, // teal
          { bg: "#dcdaf1", subBg: "#ececf8", text: "#4f46e5" }, // indigo
          { bg: "#e4dff5", subBg: "#eeeaf9", text: "#7c3aed" }, // violet
          { bg: "#f5ded0", subBg: "#faece2", text: "#533023" }, // rust
          { bg: "#d6e2f3", subBg: "#ebf1fa", text: "#1d4ed8" }, // blue
          { bg: "#d7e9db", subBg: "#ebf4ed", text: "#15803d" }, // green
        ];
    const map = new Map<string, { bg: string; subBg: string; text: string }>();
    let paletteIndex = 0;
    columnGroups.forEach((grp) => {
      if (!grp.label || map.has(grp.label)) return;
      map.set(grp.label, palette[paletteIndex % palette.length]);
      paletteIndex += 1;
    });
    return map;
  }, [columnGroups, isDark]);
  /** Same group color, indexed by column position, so a column-name cell can pick up
   *  its group's color without looking up the group again per render. */
  const columnGroupColors = useMemo(() => {
    const arr: ({ bg: string; subBg: string; text: string } | undefined)[] = [];
    let cursor = 0;
    columnGroups.forEach((grp) => {
      const color = grp.label ? groupHeaderColors.get(grp.label) : undefined;
      for (let i = 0; i < grp.span; i++) arr[cursor + i] = color;
      cursor += grp.span;
    });
    return arr;
  }, [columnGroups, groupHeaderColors]);
  const GROUP_ROW_HEIGHT = 34;
  const headerRowTopOffset = hasColumnGroups ? GROUP_ROW_HEIGHT : 0;
  const HEADER_TOTAL_HEIGHT = headerRowTopOffset + 34;
  const frozenRowTopOffsets = useMemo(() => {
    const offsets: number[] = [];
    let running = HEADER_TOTAL_HEIGHT;
    for (let i = 0; i < frozenRowCount; i++) {
      offsets.push(running);
      running += invoiceHeights[i] ?? 0;
    }
    return offsets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frozenRowCount, invoiceHeights, HEADER_TOTAL_HEIGHT]);

  /** Content-space Y (0 = first data row's top, ignoring header/scroll) of the current row-freeze boundary. */
  const frozenRowBoundaryContentY = invoiceOffsets[frozenRowCount] ?? 0;
  /** Content-space X (0 = first data column's left, ignoring row-number col/scroll) of the column-freeze boundary. */
  const frozenColBoundaryContentX =
    frozenColumnIndex >= 0
      ? columnLeftOffsets[frozenColumnIndex] + columnWidth(columns[frozenColumnIndex]) - INDEX_COL_WIDTH
      : 0;

  const scrollContainerRect = scrollRef.current?.getBoundingClientRect() ?? null;
  // The frozen region is pinned via position:sticky, so its boundary always sits at this
  // exact viewport position regardless of how far the grid has been scrolled — no scrollTop
  // subtraction here (that was the bug: it made the line drift while scrolling).
  const rowFreezeHandleViewportY =
    rowFreezeDragLineY ??
    (scrollContainerRect ? scrollContainerRect.top + HEADER_TOTAL_HEIGHT + frozenRowBoundaryContentY : null);
  const colFreezeHandleViewportX =
    colFreezeDragLineX ??
    (scrollContainerRect ? scrollContainerRect.left + INDEX_COL_WIDTH + frozenColBoundaryContentX : null);

  const handleRowFreezeHandleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const containerRect = scrollRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    setRowFreezeDragLineY(event.clientY);

    const commit = (clientY: number) => {
      const currentScrollTop = scrollRef.current?.scrollTop ?? 0;
      const contentY = clientY - containerRect.top - HEADER_TOTAL_HEIGHT + currentScrollTop;
      let count = 0;
      for (let i = 0; i < invoiceOffsets.length - 1; i++) {
        if (invoiceOffsets[i] <= contentY) count = i + 1;
        else break;
      }
      setFrozenRowCount(Math.max(0, Math.min(count, sortedRowIndices.length)));
    };

    const onMouseMove = (e: MouseEvent) => setRowFreezeDragLineY(e.clientY);
    const onMouseUp = (e: MouseEvent) => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      commit(e.clientY);
      setRowFreezeDragLineY(null);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleColFreezeHandleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const containerRect = scrollRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    setColFreezeDragLineX(event.clientX);

    const commit = (clientX: number) => {
      const currentScrollLeft = scrollRef.current?.scrollLeft ?? 0;
      const contentX = clientX - containerRect.left - INDEX_COL_WIDTH + currentScrollLeft;
      let idx = -1;
      let runningRight = 0;
      for (let i = 0; i < columns.length; i++) {
        runningRight += columnWidth(columns[i]);
        if (runningRight <= contentX) idx = i;
        else break;
      }
      setFrozenColumnId(idx >= 0 && columns[idx] ? columns[idx].id : null);
    };

    const onMouseMove = (e: MouseEvent) => setColFreezeDragLineX(e.clientX);
    const onMouseUp = (e: MouseEvent) => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      commit(e.clientX);
      setColFreezeDragLineX(null);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const tableMinWidth = INDEX_COL_WIDTH + columns.reduce((sum, col) => sum + columnWidth(col), 0);
  const loadedPct = allCount > 0 ? Math.min(100, Math.round((loadedCount / allCount) * 100)) : 0;
  const footerBg = isDark ? "#12161e" : "#f8fafc";
  const totalsRowBg = isDark ? "#1c182b" : "#eef2f6";
  const totalsRowColor = isDark ? "#5469ae" : "#6f82c0";
  const scrollbarTrack = isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1";
  const scrollbarThumb = isDark ? "rgba(45,212,191,0.7)" : "#64748b";
  const scrollbarThumbHover = isDark ? "rgba(45,212,191,0.9)" : "#334155";
  const sheetScrollSx = {
    overflow: isCompact ? "hidden" : "auto",
    flex: isCompact ? "none" : 1,
    minHeight: 0,
    width: "100%",
    backgroundColor: sheetBg,
    scrollbarGutter: "stable" as const,
    scrollbarWidth: "thin" as const,
    scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
    "&::-webkit-scrollbar": {
      width: 12,
      height: 12,
    },
    "&::-webkit-scrollbar-track": {
      background: scrollbarTrack,
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: scrollbarThumb,
      borderRadius: "8px",
      border: `2px solid ${isDark ? sheetBg : "#e2e8f0"}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: scrollbarThumbHover,
    },
    "&::-webkit-scrollbar-corner": {
      background: scrollbarTrack,
    },
  };

  const cellBaseSx = {
    borderBottom: `1px solid ${gridLine}`,
    borderRight: `1px solid ${gridLine}`,
    px: 1,
    py: 0.6,
    height: 34,
    whiteSpace: "nowrap" as const,
    fontSize: "0.8rem",
    boxSizing: "border-box" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const columnCellSx = (col: ExcelSheetColumn) => {
    const idx = columns.indexOf(col);
    const isFrozenColumn = frozenColumnIndex >= 0 && idx >= 0 && idx <= frozenColumnIndex;
    return {
      ...cellBaseSx,
      minWidth: columnWidth(col),
      textAlign: col.align || "left",
      ...(isFrozenColumn
        ? {
            position: "sticky" as const,
            left: columnLeftOffsets[idx],
            zIndex: 1,
          }
        : {}),
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isFullScreen}
      transitionDuration={0}
      PaperProps={{
        sx: {
          ...paperSx(isFullScreen),
          maxWidth: isFullScreen ? "100%" : "96vw",
          width: isFullScreen ? "100%" : "96vw",
          maxHeight: isFullScreen ? "100vh" : "92vh",
          height: isFullScreen ? "100vh" : isCompact ? "auto" : "92vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: sheetBg,
        },
      }}
      aria-labelledby="excel-sheet-title"
    >
      {!isToolbarRenderProp || title ? (
        <Box
          sx={{
            ...headerSx,
            alignItems: "center",
            justifyContent: title ? "space-between" : "flex-end",
            backgroundColor: sheetBg,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          {title ? (
            <Box sx={{ pr: 2, minWidth: 0 }}>
              <Typography
                id="excel-sheet-title"
                variant="h6"
                fontWeight={800}
                color={tokens.textPrimary}
                sx={{ lineHeight: 1.25 }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  variant="body2"
                  sx={{ mt: 0.4, color: tokens.textSecondary, fontSize: "0.78rem" }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {!isToolbarRenderProp ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              {headerActions}
              {exportButtonNode}
              {controlsButtonsNode}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {refreshing ? (
        <LinearProgress
          aria-label="Refreshing sales sheet"
          sx={{
            height: 3,
            backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(13,148,136,0.10)",
            "& .MuiLinearProgress-bar": { backgroundColor: accent },
          }}
        />
      ) : null}
      <Box
        sx={{
          px: 2,
          pb: 2,
          pt: 1.5,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "100%",
          flex: isCompact ? "none" : 1,
        }}
      >
        {showLoading ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 320,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2.5,
              animation: `${fadeIn} 0.2s ease-out`,
            }}
          >
            <Box sx={{ position: "relative", width: 96, height: 96 }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={96}
                thickness={3}
                sx={{ position: "absolute", inset: 0, color: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)" }}
              />
              <CircularProgress
                variant="determinate"
                value={loadingProgress}
                size={96}
                thickness={3}
                aria-label="Loading sales sheet"
                sx={{
                  position: "absolute",
                  inset: 0,
                  color: accent,
                  transition: "all 0.2s ease",
                  "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                }}
              />
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: accent, letterSpacing: "-0.02em" }}>
                  {Math.round(loadingProgress)}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  color: isDark ? "#e2e8f0" : "#1e293b",
                }}
              >
                Loading Sales Sheet
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  mt: 0.3,
                  color: isDark ? "rgba(226,232,240,0.6)" : "#64748b",
                  animation: `${loadingPulse} 1.6s ease-in-out infinite`,
                }}
              >
                Fetching your records, please wait…
              </Typography>
            </Box>
            <Box sx={{ width: "min(360px, 80%)" }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(13,148,136,0.10)",
                  "& .MuiLinearProgress-bar": { backgroundColor: accent, borderRadius: 2, transition: "transform 0.2s linear" },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: isCompact ? "none" : 1,
              minHeight: 0,
              width: "100%",
              border: `1px solid ${gridLine}`,
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: sheetBg,
              animation: `${fadeIn} 0.28s ease-out`,
            }}
          >
            {isToolbarRenderProp
              ? (toolbar as (exportButton: React.ReactNode, controls: React.ReactNode) => React.ReactNode)(
                  exportButtonNode,
                  controlsButtonsNode
                )
              : toolbar}
            <Box
              ref={scrollRef}
              onScroll={handleSheetScroll}
              aria-label="Sales sheet grid"
              sx={{
                ...sheetScrollSx,
                userSelect: isSelectingRange ? "none" : undefined,
                cursor: isSelectingRange ? "cell" : undefined,
                transform: "translateZ(0)",
              }}
            >
            <Table
              size="small"
              sx={{
                borderCollapse: "separate",
                borderSpacing: 0,
                tableLayout: "fixed",
                width: "100%",
                minWidth: tableMinWidth,
              }}
            >
              <colgroup>
                <col style={{ width: INDEX_COL_WIDTH }} />
                {columns.map((col) => (
                  <col key={`col-${col.id}`} style={{ width: columnWidth(col) }} />
                ))}
              </colgroup>
              <TableHead>
                {hasColumnGroups ? (
                  <TableRow>
                    <TableCell
                      sx={{
                        ...cellBaseSx,
                        width: INDEX_COL_WIDTH,
                        minWidth: INDEX_COL_WIDTH,
                        maxWidth: INDEX_COL_WIDTH,
                        bgcolor: letterBg,
                        position: "sticky",
                        left: 0,
                        top: 0,
                        zIndex: 20,
                      }}
                    />
                    {(() => {
                      let colCursor = 0;
                      return columnGroups.map((grp, i) => {
                        const startIndex = colCursor;
                        colCursor += grp.span;
                        const isGroupFrozen =
                          frozenColumnIndex >= 0 && startIndex + grp.span - 1 <= frozenColumnIndex;
                        const groupColor = grp.label ? groupHeaderColors.get(grp.label) : undefined;
                        return (
                      <TableCell
                        key={`group-${i}`}
                        colSpan={grp.span}
                        sx={{
                          ...cellBaseSx,
                          bgcolor: groupColor?.bg ?? letterBg,
                          color: groupColor?.text ?? tokens.textSecondary,
                          fontWeight: 800,
                          textAlign: "center",
                          position: "sticky",
                          top: 0,
                          left: isGroupFrozen ? columnLeftOffsets[startIndex] : undefined,
                          zIndex: isGroupFrozen ? 16 : 15,
                          letterSpacing: "0.03em",
                          fontSize: "0.72rem",
                          borderBottom: `2px solid ${headerSectionDividerColor}`,
                        }}
                      >
                        {grp.label}
                        <Box
                          onMouseDown={(e) => handleColumnResizeMouseDown(e, grp.lastCol)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: 6,
                            cursor: "col-resize",
                            zIndex: 6,
                            "&:hover": { backgroundColor: accent },
                          }}
                        />
                      </TableCell>
                        );
                      });
                    })()}
                  </TableRow>
                ) : null}
                <TableRow>
                  <TableCell
                    sx={{
                      ...cellBaseSx,
                      width: INDEX_COL_WIDTH,
                      minWidth: INDEX_COL_WIDTH,
                      maxWidth: INDEX_COL_WIDTH,
                      bgcolor: rowNumBg,
                      color: tokens.textSecondary,
                      fontWeight: 700,
                      textAlign: "right",
                      position: "sticky",
                      left: 0,
                      top: headerRowTopOffset,
                      zIndex: 20,
                    }}
                  />
                  {columns.map((col, colIndex) => {
                    const canFilter = !col.subRowsFrom;
                    const isSorted = sortState?.columnId === col.id;
                    const isFiltered = !!columnFilters[col.id];
                    const isFrozenHeaderColumn = frozenColumnIndex >= 0 && colIndex <= frozenColumnIndex;
                    return (
                      <TableCell
                        key={`label-${col.id}`}
                        data-column-id={col.id}
                        onClick={() => {
                          if (dragSuppressClickRef.current) {
                            dragSuppressClickRef.current = false;
                            return;
                          }
                          handleSelectColumn(colIndex);
                        }}
                        onMouseDown={handleColumnHeaderMouseDown(col)}
                        sx={{
                          ...columnCellSx(col),
                          color: columnGroupColors[colIndex]?.text ?? headerLabelColor,
                          fontWeight: 800,
                          letterSpacing: "0.02em",
                          fontSize: "0.74rem",
                          position: "sticky",
                          top: headerRowTopOffset,
                          zIndex: isFrozenHeaderColumn ? 16 : 15,
                          // Solid (not rgba) so the sticky header stays opaque during vertical
                          // scroll — a translucent background let scrolled-under row text bleed
                          // through the frozen header cell once a column filter was applied.
                          bgcolor: isFiltered
                            ? isDark
                              ? "#241c0f"
                              : "#fdf1dc"
                            : lockedCols.has(colIndex)
                              ? lockedIndicatorBg
                              : (columnGroupColors[colIndex]?.subBg ?? sheetBg),
                          cursor: draggingColumnId
                            ? "grabbing"
                            : isDark
                              ? COLUMN_SELECT_CURSOR_DARK
                              : COLUMN_SELECT_CURSOR_LIGHT,
                          userSelect: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 0.2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.2,
                              overflow: "hidden",
                              minWidth: 0,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {toSheetHeaderLabel(col.label)}
                            </Box>
                            {isSorted ? (
                              sortState!.direction === "asc" ? (
                                <ArrowUpwardIcon sx={{ fontSize: 13, color: accent, flexShrink: 0 }} />
                              ) : (
                                <ArrowDownwardIcon sx={{ fontSize: 13, color: accent, flexShrink: 0 }} />
                              )
                            ) : null}
                          </Box>
                          {canFilter ? (
                            <Tooltip title={isFiltered ? "Filter applied" : ""} disableHoverListener={!isFiltered}>
                              <IconButton
                                size="small"
                                draggable={false}
                                aria-label={`Filter ${col.label}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFilterMenu(col.id, e.currentTarget);
                                }}
                                sx={{
                                  p: 0.15,
                                  flexShrink: 0,
                                  color: isFiltered ? "#f59e0b" : headerLabelColor,
                                  cursor: "pointer",
                                }}
                              >
                                {isFiltered ? (
                                  <FilterAltIcon sx={{ fontSize: 15 }} />
                                ) : (
                                  <ArrowDropDownIcon sx={{ fontSize: 18 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Box
                          draggable={false}
                          onMouseDown={(e) => handleColumnResizeMouseDown(e, col)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: 6,
                            cursor: "col-resize",
                            zIndex: 6,
                            "&:hover": { backgroundColor: accent },
                          }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sheetRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      sx={{ ...cellBaseSx, textAlign: "center", color: tokens.textSecondary, py: 2 }}
                    >
                      {emptyText}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {sortedRowIndices.slice(0, frozenRowCount).map((rowIndex, displayPosition) => {
                      const row = sheetRows[rowIndex];
                      return (
                        <ExcelInvoiceRow
                          key={String(row.invoiceId ?? rowIndex)}
                          row={row}
                          rowIndex={rowIndex}
                          excelRowStart={excelRowStarts[displayPosition]}
                          columns={columns}
                          cellBaseSx={cellBaseSx}
                          columnCellSx={columnCellSx}
                          gridLine={gridLine}
                          rowNumBg={rowNumBg}
                          editableColor={editableColor}
                          readOnlyColor={readOnlyColor}
                          isDark={isDark}
                          indexColWidth={INDEX_COL_WIDTH}
                          onCellChange={handleCellChange}
                          onCellBlur={handleCellInputBlur}
                          isDirty={isRowDirty(rowIndex)}
                          isSaving={savingRowIndexes.has(rowIndex)}
                          errorMessage={rowErrors[rowIndex]}
                          onSelectRow={handleSelectRow}
                          hasRowActions={!!getRowActions}
                          onRowContextMenu={handleRowContextMenu}
                          onSaveRow={handleSaveRow}
                          onCancelRow={handleCancelRow}
                          editingColumnId={editingCell?.rowIndex === rowIndex ? editingCell.columnId : null}
                          invalidColumnId={invalidCell?.rowIndex === rowIndex ? invalidCell.columnId : null}
                          invalidMessage={invalidCell?.rowIndex === rowIndex ? invalidCell.message : undefined}
                          selectionBounds={selectionBounds}
                          showEditIcon={!!onRowClick}
                          highlightRowKey={highlightRowKey}
                          onRowClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                          onPopupEditClick={
                            onCellPopupEdit
                              ? (_ri, columnId, subRow) => onCellPopupEdit(row, rowIndex, columnId, subRow)
                              : undefined
                          }
                          onCellMouseDown={handleCellMouseDown}
                          onCellMouseEnter={handleCellMouseEnter}
                          onCellKeyDown={handleCellKeyDown}
                          onFeeContextMenu={handleFeeContextMenu}
                          onCellContextMenu={handleCellContextMenu}
                          onFeeClick={handleFeeClick}
                          activeFeeCell={activeFeeCell}
                          onInlineFeeEditClick={handleInlineFeeEditClick}
                          onInlineFeeEditSecondaryClick={handleInlineFeeEditSecondaryClick}
                          onFeeEditDirect={onFeeEdit}
                          accent={accent}
                          lineHeight={getRowLineHeight(rowIndex)}
                          onRowResizeMouseDown={handleRowResizeMouseDown}
                          frozenColumnIndex={frozenColumnIndex}
                          frozenCellBg={frozenCellBg}
                          stickyTop={frozenRowTopOffsets[displayPosition]}
                          isRowLocked={lockedRows.has(rowIndex)}
                          lockedCols={lockedCols}
                          pickedCols={pickedColsByRow.get(rowIndex)}
                          lockedCellBg={lockedCellBg}
                          lockedIndicatorBg={lockedIndicatorBg}
                        />
                      );
                    })}
                    {visibleRange.start > frozenRowCount ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + 1}
                          sx={{
                            p: 0,
                            border: 0,
                            height: invoiceOffsets[visibleRange.start] - invoiceOffsets[frozenRowCount],
                          }}
                        />
                      </TableRow>
                    ) : null}
                    {sortedRowIndices
                      .slice(Math.max(visibleRange.start, frozenRowCount), visibleRange.end)
                      .map((rowIndex, sliceIndex) => {
                        const row = sheetRows[rowIndex];
                        const displayPosition = Math.max(visibleRange.start, frozenRowCount) + sliceIndex;
                        return (
                          <ExcelInvoiceRow
                            key={String(row.invoiceId ?? rowIndex)}
                            row={row}
                            rowIndex={rowIndex}
                            excelRowStart={excelRowStarts[displayPosition]}
                            columns={columns}
                            cellBaseSx={cellBaseSx}
                            columnCellSx={columnCellSx}
                            gridLine={gridLine}
                            rowNumBg={rowNumBg}
                            editableColor={editableColor}
                            readOnlyColor={readOnlyColor}
                            isDark={isDark}
                            indexColWidth={INDEX_COL_WIDTH}
                            onCellChange={handleCellChange}
                            onCellBlur={handleCellInputBlur}
                            isDirty={isRowDirty(rowIndex)}
                            isSaving={savingRowIndexes.has(rowIndex)}
                            errorMessage={rowErrors[rowIndex]}
                            onSelectRow={handleSelectRow}
                            hasRowActions={!!getRowActions}
                            onRowContextMenu={handleRowContextMenu}
                            onSaveRow={handleSaveRow}
                            onCancelRow={handleCancelRow}
                            editingColumnId={editingCell?.rowIndex === rowIndex ? editingCell.columnId : null}
                            invalidColumnId={invalidCell?.rowIndex === rowIndex ? invalidCell.columnId : null}
                            invalidMessage={invalidCell?.rowIndex === rowIndex ? invalidCell.message : undefined}
                            selectionBounds={selectionBounds}
                            showEditIcon={!!onRowClick}
                            highlightRowKey={highlightRowKey}
                            onRowClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                            onPopupEditClick={
                              onCellPopupEdit
                                ? (_ri, columnId, subRow) => onCellPopupEdit(row, rowIndex, columnId, subRow)
                                : undefined
                            }
                            onCellMouseDown={handleCellMouseDown}
                            onCellMouseEnter={handleCellMouseEnter}
                            onCellKeyDown={handleCellKeyDown}
                            onFeeContextMenu={handleFeeContextMenu}
                            onCellContextMenu={handleCellContextMenu}
                            onFeeClick={handleFeeClick}
                            activeFeeCell={activeFeeCell}
                            onInlineFeeEditClick={handleInlineFeeEditClick}
                            onInlineFeeEditSecondaryClick={handleInlineFeeEditSecondaryClick}
                            onFeeEditDirect={onFeeEdit}
                            accent={accent}
                            lineHeight={getRowLineHeight(rowIndex)}
                            onRowResizeMouseDown={handleRowResizeMouseDown}
                            frozenColumnIndex={frozenColumnIndex}
                            frozenCellBg={frozenCellBg}
                            isRowLocked={lockedRows.has(rowIndex)}
                            lockedCols={lockedCols}
                            pickedCols={pickedColsByRow.get(rowIndex)}
                            lockedCellBg={lockedCellBg}
                            lockedIndicatorBg={lockedIndicatorBg}
                          />
                        );
                      })}
                    {visibleRange.end < sheetRows.length ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + 1}
                          sx={{
                            p: 0,
                            border: 0,
                            height: invoiceOffsets[sheetRows.length] - invoiceOffsets[visibleRange.end],
                          }}
                        />
                      </TableRow>
                    ) : null}
                  </>
                )}
              </TableBody>
              {showTotals && sheetRows.length > 0 ? (
                <TableFooter>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...cellBaseSx,
                        width: INDEX_COL_WIDTH,
                        minWidth: INDEX_COL_WIDTH,
                        maxWidth: INDEX_COL_WIDTH,
                        position: "sticky",
                        left: 0,
                        bottom: 0,
                        zIndex: 17,
                        bgcolor: totalsRowBg,
                        color: totalsRowColor,
                        fontWeight: 800,
                        fontSize: "0.72rem",
                      }}
                    >
                      TOTAL
                    </TableCell>
                    {columns.map((col) => {
                      const idx = columns.indexOf(col);
                      const isFrozenColumn = frozenColumnIndex >= 0 && idx >= 0 && idx <= frozenColumnIndex;
                      return (
                        <TableCell
                          key={`total-cell-${col.id}`}
                          sx={{
                            ...cellBaseSx,
                            minWidth: columnWidth(col),
                            textAlign: col.align || "left",
                            position: "sticky",
                            bottom: 0,
                            zIndex: isFrozenColumn ? 16 : 15,
                            ...(isFrozenColumn ? { left: INDEX_COL_WIDTH } : {}),
                            bgcolor: totalsRowBg,
                            color: col.total ? totalsRowColor : "transparent",
                            fontWeight: 800,
                          }}
                        >
                          {col.total ? (totals[col.id] || "—") : ""}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
            </Box>
            {sheetRows.length > 0 && (showTotals || showFooter) ? (
              <Box
                sx={{
                  flexShrink: 0,
                  width: "100%",
                  borderTop: `1px solid ${gridLine}`,
                  backgroundColor: footerBg,
                }}
              >
                {showFooter ? (
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      px: 2,
                      pt: 1.5,
                      pb: 1.75,
                      borderBottom: showTotals ? `1px solid ${gridLine}` : "none",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, maxWidth: { xs: "100%", sm: "46%" } }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "baseline",
                          gap: 0.5,
                          flexShrink: 0,
                          px: 1.3,
                          py: 0.55,
                          borderRadius: "999px",
                          border: `1px solid ${isDark ? "rgba(20,184,166,0.32)" : "rgba(13,148,136,0.22)"}`,
                          backgroundColor: isDark ? "rgba(20,184,166,0.10)" : "rgba(13,148,136,0.06)",
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: accent }}>
                          {sortedRowIndices.length.toLocaleString()}
                        </Typography>
                        <Typography sx={{ fontSize: "0.76rem", color: tokens.textSecondary, whiteSpace: "nowrap" }}>
                          / {allCount.toLocaleString()} records
                          {sortedRowIndices.length !== loadedCount
                            ? ` (filtered from ${loadedCount.toLocaleString()} loaded)`
                            : ""}
                        </Typography>
                      </Box>
                      <Box
                        aria-hidden
                        sx={{
                          height: 5,
                          flex: 1,
                          minWidth: 60,
                          maxWidth: 260,
                          borderRadius: "999px",
                          backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.07)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${loadedPct}%`,
                            height: "100%",
                            borderRadius: "999px",
                            background: "linear-gradient(90deg, #14b8a6 0%, #0d9488 100%)",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </Box>
                    </Box>
                    {hasMore ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={handleLoadMore}
                        disabled={loadMoreLoading}
                        aria-label={`Load More (Next ${nextBatch})`}
                        sx={{
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.6,
                          border: `1px solid ${isDark ? "rgba(148,163,184,0.28)" : "rgba(15,23,42,0.14)"}`,
                          backgroundColor: "transparent",
                          color: tokens.textSecondary,
                          borderRadius: "999px",
                          px: 1.6,
                          py: 0.55,
                          fontWeight: 700,
                          fontSize: "0.78rem",
                          cursor: loadMoreLoading ? "not-allowed" : "pointer",
                          opacity: loadMoreLoading ? 0.7 : 1,
                          transition: "all 0.15s ease",
                          "&:hover": {
                            color: accent,
                            borderColor: accent,
                            backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(13,148,136,0.06)",
                          },
                        }}
                      >
                        {loadMoreLoading ? (
                          <CircularProgress size={14} sx={{ color: "currentColor" }} aria-label="Loading more invoices" />
                        ) : (
                          <KeyboardDoubleArrowDownIcon sx={{ fontSize: 16 }} />
                        )}
                        Load More (Next {nextBatch})
                      </Box>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
      <Popover
        open={rowActionsMenu !== null}
        onClose={closeRowActionsMenu}
        transitionDuration={0}
        anchorReference="anchorPosition"
        anchorPosition={
          rowActionsMenu !== null ? { top: rowActionsMenu.mouseY, left: rowActionsMenu.mouseX } : undefined
        }
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              minWidth: 210,
              borderRadius: "12px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.55)" : "0 8px 28px rgba(15,23,42,0.18)",
              overflow: "hidden",
              py: 0.5,
            },
          },
        }}
      >
        {rowActionsMenu ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 1,
                mt: -0.5,
                background: isDark
                  ? "linear-gradient(135deg, rgba(20,184,166,0.18), rgba(20,184,166,0.02))"
                  : "linear-gradient(135deg, rgba(13,148,136,0.12), rgba(13,148,136,0.02))",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: isDark ? "rgba(226,232,240,0.8)" : "rgba(15,23,42,0.7)",
                }}
              >
                Invoice Actions
              </Typography>
            </Box>
            <Divider sx={{ borderColor: gridLine, mb: 0.5 }} />
          </>
        ) : null}
        {rowActionsMenu &&
          getRowActions?.(sheetRows[rowActionsMenu.rowIndex], rowActionsMenu.rowIndex).map((action) => (
            <Box
              key={action.key}
              role="button"
              aria-label={action.label}
              onClick={() => {
                closeRowActionsMenu();
                action.onClick();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.85,
                cursor: "pointer",
                "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
              }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  ...(action.iconBackground
                    ? { background: action.iconBackground, border: "none" }
                    : {
                        bgcolor: "transparent",
                        border: `1.5px solid ${action.iconColor || accent}`,
                      }),
                  color: action.iconColor || accent,
                }}
              >
                {action.icon}
              </Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: isDark ? "#f8fafc" : "#0f172a" }}>
                {action.label}
              </Typography>
            </Box>
          ))}
      </Popover>
      {(() => {
        const feeTooltipCol = columns.find((c) => c.id === feeTooltip?.columnId);
        return (
      <Popover
        open={feeTooltip !== null}
        onClose={closeFeeTooltip}
        transitionDuration={0}
        anchorReference="anchorPosition"
        anchorPosition={
          feeTooltip !== null ? { top: feeTooltip.mouseY, left: feeTooltip.mouseX } : undefined
        }
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              borderRadius: "10px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.55)" : "0 8px 28px rgba(15,23,42,0.18)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          role="button"
          aria-label={`Edit ${feeTooltip?.label ?? ""}`}
          onClick={handleFeeEditClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.75,
            cursor: "pointer",
            borderBottom: feeTooltip?.secondary ? `1px solid ${gridLine}` : "none",
            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)" },
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: feeTooltipCol?.feeIcon === "edit" ? editAccent : accent,
              color: "#fff",
            }}
          >
            {feeTooltipCol?.feeIcon === "edit" ? (
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
            ) : (
              <PaidOutlinedIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a" }}>
            {feeTooltip?.label}
          </Typography>
        </Box>
        {feeTooltip?.secondary ? (
          <Box
            role="button"
            aria-label={feeTooltip.secondary.label}
            onClick={handleFeeEditSecondaryClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              cursor: "pointer",
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)" },
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                bgcolor: feeTooltip.secondary.icon === "edit" ? editAccent : accent,
                color: "#fff",
              }}
            >
              {feeTooltip.secondary.icon === "edit" ? (
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              ) : (
                <PaidOutlinedIcon sx={{ fontSize: 14 }} />
              )}
            </Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a" }}>
              {feeTooltip.secondary.label}
            </Typography>
          </Box>
        ) : null}
        {feeTooltip ? selectionMenuActionItems : null}
        {feeTooltip ? selectionMenuItems : null}
      </Popover>
        );
      })()}
      <Popover
        open={cellContextMenu !== null}
        onClose={closeCellContextMenu}
        transitionDuration={0}
        anchorReference="anchorPosition"
        anchorPosition={
          cellContextMenu !== null
            ? { top: cellContextMenu.mouseY, left: cellContextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              display: "flex",
              flexDirection: "column",
              minWidth: 140,
              borderRadius: "10px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.55)" : "0 8px 28px rgba(15,23,42,0.18)",
              overflow: "hidden",
              py: 0.5,
            },
          },
        }}
      >
        <Box
          role="button"
          aria-label="Copy"
          onClick={handleCopyFromContextMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.75,
            fontSize: "0.82rem",
            cursor: "pointer",
            color: isDark ? "#f8fafc" : "#0f172a",
            "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
          }}
        >
          Copy
        </Box>
        {cellContextMenu?.canEdit ? (
          <Box
            role="button"
            aria-label="Edit"
            onClick={handleEditFromContextMenu}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              fontSize: "0.82rem",
              cursor: "pointer",
              color: isDark ? "#f8fafc" : "#0f172a",
              "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
            }}
          >
            Edit
          </Box>
        ) : null}
        {cellContextMenu ? selectionMenuActionItems : null}
        {cellContextMenu ? selectionMenuItems : null}
      </Popover>
      <Popover
        open={filterMenu !== null}
        onClose={handleCloseFilterMenu}
        anchorEl={filterMenu?.anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 270,
              mt: 0.5,
              borderRadius: "14px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.6)" : "0 12px 32px rgba(15,23,42,0.16)",
              overflow: "hidden",
              bgcolor: isDark ? "#161b24" : "#ffffff",
            },
          },
        }}
      >
        {filterMenuColumn ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 0.75 }}>
              {(() => {
                const labels = getSortLabels(filterMenuColumn);
                const activeDirection = sortState?.columnId === filterMenuColumn.id ? sortState.direction : null;
                return (
                  <>
                    <Box
                      role="button"
                      onClick={() => handleSortFromMenu("asc")}
                      sx={{
                        ...filterMenuItemSx,
                        bgcolor:
                          activeDirection === "asc"
                            ? isDark
                              ? "rgba(45,212,191,0.16)"
                              : "rgba(13,148,136,0.10)"
                            : "transparent",
                        fontWeight: activeDirection === "asc" ? 700 : 400,
                      }}
                    >
                      <ArrowUpwardIcon sx={{ fontSize: 15, color: accent }} />
                      {labels.asc}
                    </Box>
                    <Box
                      role="button"
                      onClick={() => handleSortFromMenu("desc")}
                      sx={{
                        ...filterMenuItemSx,
                        bgcolor:
                          activeDirection === "desc"
                            ? isDark
                              ? "rgba(45,212,191,0.16)"
                              : "rgba(13,148,136,0.10)"
                            : "transparent",
                        fontWeight: activeDirection === "desc" ? 700 : 400,
                      }}
                    >
                      <ArrowDownwardIcon sx={{ fontSize: 15, color: accent }} />
                      {labels.desc}
                    </Box>
                  </>
                );
              })()}
            </Box>
            {filterMenuColumn.colorFrom ? (
              <>
                <Divider sx={{ borderColor: gridLine }} />
                <Box sx={{ p: 0.75 }}>
                  <Box
                    role="button"
                    onClick={(e) => setColorSortSubmenuAnchor(e.currentTarget)}
                    sx={{ ...filterMenuItemSx, justifyContent: "space-between" }}
                  >
                    Sort By Color
                    <ChevronRightIcon sx={{ fontSize: 16, color: tokens.textSecondary }} />
                  </Box>
                </Box>
              </>
            ) : null}
            <Divider sx={{ borderColor: gridLine }} />
            <Box sx={{ p: 0.75 }}>
              <Box
                role="button"
                onClick={handleClearFilterFromMenu}
                aria-disabled={!columnFilters[filterMenuColumn.id]}
                sx={{
                  ...filterMenuItemSx,
                  color: columnFilters[filterMenuColumn.id] ? "inherit" : tokens.textSecondary,
                  opacity: columnFilters[filterMenuColumn.id] ? 1 : 0.5,
                  cursor: columnFilters[filterMenuColumn.id] ? "pointer" : "default",
                  pointerEvents: columnFilters[filterMenuColumn.id] ? "auto" : "none",
                }}
              >
                <FilterAltOffOutlinedIcon sx={{ fontSize: 15, color: tokens.textSecondary }} />
                {`Clear Filter from '${filterMenuColumn.label}'`}
              </Box>
            </Box>
            <Divider sx={{ borderColor: gridLine }} />
            <Box sx={{ px: 1.25, py: 1 }}>
              <TextField
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Search"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: tokens.textSecondary }} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
                  },
                }}
              />
            </Box>
            <Box sx={{ maxHeight: 220, overflowY: "auto", px: 0.75, pb: 0.5 }}>
              <Box role="button" onClick={toggleFilterSelectAll} sx={filterMenuCheckRowSx}>
                <Checkbox size="small" checked={isFilterSelectAllChecked} tabIndex={-1} sx={filterMenuCheckboxSx} />
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Select All</Typography>
              </Box>
              {filterMenuVisibleOptions.map((opt) => (
                <Box key={opt.key} role="button" onClick={() => toggleFilterOption(opt.key)} sx={filterMenuCheckRowSx}>
                  <Checkbox
                    size="small"
                    checked={isFilterOptionChecked(opt.key)}
                    tabIndex={-1}
                    sx={filterMenuCheckboxSx}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Box>
              ))}
              {filterMenuVisibleOptions.length === 0 ? (
                <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary, px: 1, py: 1 }}>
                  No matches
                </Typography>
              ) : null}
            </Box>
            <Divider sx={{ borderColor: gridLine }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 1.25, py: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleApplyFilterFromMenu}
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  boxShadow: "none",
                  bgcolor: accent,
                  "&:hover": { bgcolor: accent, opacity: 0.9, boxShadow: "none" },
                }}
              >
                Apply
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleCloseFilterMenu}
                sx={{ borderRadius: "999px", textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : null}
      </Popover>
      <Popover
        open={colorSortSubmenuAnchor !== null}
        onClose={() => setColorSortSubmenuAnchor(null)}
        anchorEl={colorSortSubmenuAnchor}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 210,
              borderRadius: "14px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.6)" : "0 12px 32px rgba(15,23,42,0.16)",
              overflow: "hidden",
              bgcolor: isDark ? "#161b24" : "#ffffff",
              p: 1,
            },
          },
        }}
      >
        {filterMenuColumn ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, px: 0.5, pb: 0.75 }}>
              Sort By Font Color
            </Typography>
            <Box sx={{ display: "flex", gap: 1, px: 0.5, pb: 0.75 }}>
              {Object.entries(INVOICE_PAY_STATUS_COLORS).map(([status, color]) => {
                const isActive =
                  colorSortState?.columnId === filterMenuColumn.id && colorSortState.priorityStatus === status;
                return (
                  <Box
                    key={status}
                    role="button"
                    title={`${status}`}
                    onClick={() => handleSortByColorFromMenu(status)}
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "4px",
                      bgcolor: color,
                      cursor: "pointer",
                      border: isActive ? `2px solid ${accent}` : `1px solid ${gridLine}`,
                    }}
                  />
                );
              })}
            </Box>
            <Divider sx={{ borderColor: gridLine, mb: 0.5 }} />
            <Box role="button" onClick={handleClearColorSortFromMenu} sx={filterMenuItemSx}>
              Automatic
            </Box>
          </Box>
        ) : null}
      </Popover>
      {typeof document !== "undefined" &&
      (dragColumnOverlayRect || dragTargetOverlayRect || scrollContainerRect)
        ? createPortal(
            <>
              {!showLoading && rowFreezeHandleViewportY !== null && scrollContainerRect && (frozenRowCount > 0 || freezeDragEnabled) ? (
                <>
                  <Box
                    onMouseDown={freezeDragEnabled ? handleRowFreezeHandleMouseDown : undefined}
                    title={freezeDragEnabled ? "Drag to freeze rows" : undefined}
                    sx={{
                      position: "fixed",
                      left: scrollContainerRect.left,
                      top: rowFreezeHandleViewportY - 3,
                      width: scrollContainerRect.width,
                      height: 3,
                      bgcolor:
                        rowFreezeDragLineY !== null ? accent : frozenRowCount > 0 ? `${accent}55` : "transparent",
                      cursor: freezeDragEnabled ? "row-resize" : "default",
                      pointerEvents: freezeDragEnabled ? "auto" : "none",
                      zIndex: 1500,
                      "&:hover": freezeDragEnabled ? { bgcolor: accent } : {},
                    }}
                  />
                  {rowFreezeDragLineY !== null ? (
                    <Box
                      sx={{
                        position: "fixed",
                        left: scrollContainerRect.left,
                        top: rowFreezeDragLineY,
                        width: scrollContainerRect.width,
                        height: 2,
                        bgcolor: accent,
                        pointerEvents: "none",
                        zIndex: 1500,
                      }}
                    />
                  ) : null}
                </>
              ) : null}
              {!showLoading &&
              colFreezeHandleViewportX !== null &&
              scrollContainerRect &&
              (frozenColumnIndex >= 0 || freezeDragEnabled) ? (
                <>
                  <Box
                    onMouseDown={freezeDragEnabled ? handleColFreezeHandleMouseDown : undefined}
                    title={freezeDragEnabled ? "Drag to freeze columns" : undefined}
                    sx={{
                      position: "fixed",
                      left: colFreezeHandleViewportX - 3,
                      top: scrollContainerRect.top,
                      width: 3,
                      height: scrollContainerRect.height,
                      bgcolor:
                        colFreezeDragLineX !== null ? accent : frozenColumnIndex >= 0 ? `${accent}55` : "transparent",
                      cursor: freezeDragEnabled ? "col-resize" : "default",
                      pointerEvents: freezeDragEnabled ? "auto" : "none",
                      zIndex: 1500,
                      "&:hover": freezeDragEnabled ? { bgcolor: accent } : {},
                    }}
                  />
                  {colFreezeDragLineX !== null ? (
                    <Box
                      sx={{
                        position: "fixed",
                        left: colFreezeDragLineX,
                        top: scrollContainerRect.top,
                        width: 2,
                        height: scrollContainerRect.height,
                        bgcolor: accent,
                        pointerEvents: "none",
                        zIndex: 1500,
                      }}
                    />
                  ) : null}
                </>
              ) : null}
              {dragColumnOverlayRect ? (
                <Box
                  sx={{
                    position: "fixed",
                    left: dragColumnOverlayRect.left,
                    top: dragColumnOverlayRect.top,
                    width: dragColumnOverlayRect.width,
                    height: dragColumnOverlayRect.height,
                    bgcolor: isDark ? "rgba(45,212,191,0.05)" : "rgba(13,148,136,0.07)",
                    border: `1px solid ${accent}33`,
                    pointerEvents: "none",
                    zIndex: 1400,
                  }}
                />
              ) : null}
              {dragTargetOverlayRect ? (
                <Box
                  sx={{
                    position: "fixed",
                    left: dragTargetOverlayRect.left,
                    top: dragTargetOverlayRect.top,
                    width: dragTargetOverlayRect.width,
                    height: dragTargetOverlayRect.height,
                    boxShadow: `inset 2px 0 0 0 ${accent}, inset -2px 0 0 0 ${accent}`,
                    pointerEvents: "none",
                    zIndex: 1400,
                    transition: "left 0.12s ease, width 0.12s ease",
                  }}
                />
              ) : null}
            </>,
            document.body
          )
        : null}
      <Popover
        open={freezeMenuAnchor !== null}
        onClose={() => setFreezeMenuAnchor(null)}
        anchorEl={freezeMenuAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 1600 }}
        slotProps={{
          paper: {
            sx: {
              width: 260,
              mt: 0.5,
              borderRadius: "14px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.6)" : "0 12px 32px rgba(15,23,42,0.16)",
              overflow: "hidden",
              bgcolor: isDark ? "#161b24" : "#ffffff",
              p: 0.75,
            },
          },
        }}
      >
        <Box role="button" onClick={handleFreezeAtSelection} sx={filterMenuItemSx}>
          <AcUnitIcon sx={{ fontSize: 16, color: accent }} />
          Freeze at selection
        </Box>
        <Divider sx={{ borderColor: gridLine, my: 0.5 }} />
        <Box role="button" onClick={handleFreezeTopRow} sx={filterMenuItemSx}>
          Top Row
        </Box>
        <Box role="button" onClick={handleOpenFreezeRowSubmenu} sx={{ ...filterMenuItemSx, justifyContent: "space-between" }}>
          {selectedExcelRowLabel === null ? "Up to Row..." : `Up to Row ${selectedExcelRowLabel}`}
          <ChevronRightIcon sx={{ fontSize: 16, color: tokens.textSecondary }} />
        </Box>
        <Divider sx={{ borderColor: gridLine, my: 0.5 }} />
        <Box role="button" onClick={handleFreezeFirstColumn} sx={filterMenuItemSx}>
          First Column
        </Box>
        <Box
          role="button"
          onClick={(e) => setFreezeColumnSubmenuAnchor(e.currentTarget)}
          sx={{ ...filterMenuItemSx, justifyContent: "space-between" }}
        >
          {selectedColLetterLabel === null ? "Up to Column..." : `Up to Column ${selectedColLetterLabel}`}
          <ChevronRightIcon sx={{ fontSize: 16, color: tokens.textSecondary }} />
        </Box>
        <Divider sx={{ borderColor: gridLine, my: 0.5 }} />
        <Box
          role="button"
          onClick={handleUnfreezePanes}
          aria-disabled={!frozenColumnId && frozenRowCount === 0}
          sx={{
            ...filterMenuItemSx,
            color: !frozenColumnId && frozenRowCount === 0 ? tokens.textSecondary : "inherit",
            opacity: !frozenColumnId && frozenRowCount === 0 ? 0.5 : 1,
            pointerEvents: !frozenColumnId && frozenRowCount === 0 ? "none" : "auto",
          }}
        >
          Unfreeze Panes
        </Box>
      </Popover>
      <Popover
        open={freezeRowSubmenuAnchor !== null}
        onClose={() => setFreezeRowSubmenuAnchor(null)}
        anchorEl={freezeRowSubmenuAnchor}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: 1600 }}
        slotProps={{
          paper: {
            sx: {
              width: 220,
              borderRadius: "14px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.6)" : "0 12px 32px rgba(15,23,42,0.16)",
              overflow: "hidden",
              bgcolor: isDark ? "#161b24" : "#ffffff",
              p: 1.25,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Freeze up to row</Typography>
          <TextField
            value={freezeRowInput}
            onChange={(e) => setFreezeRowInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleApplyFreezeRowInput()}
            type="number"
            size="small"
            placeholder="Row number"
            autoFocus
          />
          <Button size="small" variant="contained" onClick={handleApplyFreezeRowInput} sx={{ borderRadius: "999px", boxShadow: "none" }}>
            Apply
          </Button>
        </Box>
      </Popover>
      <Popover
        open={freezeColumnSubmenuAnchor !== null}
        onClose={() => setFreezeColumnSubmenuAnchor(null)}
        anchorEl={freezeColumnSubmenuAnchor}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: 1600 }}
        slotProps={{
          paper: {
            sx: {
              width: 220,
              maxHeight: 300,
              overflowY: "auto",
              borderRadius: "14px",
              border: `1px solid ${gridLine}`,
              boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.6)" : "0 12px 32px rgba(15,23,42,0.16)",
              bgcolor: isDark ? "#161b24" : "#ffffff",
              p: 0.75,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {columns.map((col) => (
            <Box
              key={col.id}
              role="button"
              onClick={() => handlePickFreezeColumn(col.id)}
              sx={{
                ...filterMenuItemSx,
                fontWeight: frozenColumnId === col.id ? 700 : 400,
                color: frozenColumnId === col.id ? accent : "inherit",
              }}
            >
              {col.label}
            </Box>
          ))}
        </Box>
      </Popover>
    </Dialog>
  );
};

export default ExcelSheetPopup;
